/**
 * Phase Ads Stage 1 — 純型別 + enum + 常數定義
 * -----------------------------------------------------------------------------
 * 對齊 docs/31 §3.3 + docs/29 §5 + ADR-021 / ADR-023。
 *
 * **R42.1 backend pivot**(2026-05-16):
 * Backend MR !26 把 4 條 OAuth endpoint 收斂成單一 `POST /api/ads/google/oauth/link`,
 * 同步回 DISCOVERED state(沒 PENDING/TOKEN_OK/DISCOVERING 狀態機,沒 anchor listener)。
 *
 * 拆檔策略對齊 R34 job 系列:`ads.types.ts`(本檔)/ `ads.dtos.ts` / `ads.mappers.ts`
 * / `ads.ts`(barrel)。
 */

// ===== Common ===== (docs/09 §5 ads namespace + Stage 2/3 共用)

/**
 * 所有已知 Ads error class 的 single source of truth(`as const` tuple)。
 *
 * `AdsErrorClass` union 由本 tuple 派生(見下方 `(typeof ADS_ERROR_CLASSES)[number]`),
 * 所以**只需在這一個 array 增減成員**,union 型別 + runtime 白名單(`isKnownAdsErrorClass`)
 * 會自動同步,不再有「手動維護 union 跟 array 兩份、漏改一邊就漂移」的風險。
 *
 * 對齊 docs/09 §5 + docs/31 §6.1 + R42.1 backend pivot 對齊表。
 *
 * **重要**:Ads namespace 跟 workflow `StepErrorClass` **不共用值集合 / i18n key**;
 * i18n key 走 `ads.error_class.*` 不能用 `jobs.errorClass.*`。
 *
 * **歷史**:R42.0 砍掉 `STATE_INVALID` / `OAUTH_DENIED`(舊 OAuth callback URL query
 * 機制 + GET /discovery/{id} 狀態查詢,Wave 1 pivot 後不存在)。
 */
export const ADS_ERROR_CLASSES = [
  // === /oauth/link 階段(R42.1;對應 POST /oauth/link 的 401 / 502 / 500)===
  'AUTH_BROKEN',
  'LINK_UPSTREAM_FAILED',
  'LINK_INTERNAL_FAILED',
  // R289(FB-20260827-0001)— 409:授權成功但該 Google 帳號沒有可用的 Ads 帳戶。
  // ⚠️ **永久錯**,跟同族的 502 `LINK_UPSTREAM_FAILED`(暫時錯)語意相反:登記在這裡
  // 只是給「不是 /ads/connect 的路徑撞到它」時有句人話,**正常路徑走說明卡**
  // (`parseLinkNoUsableAccount` + `AdsLinkBlockedCard`),不走 toast、不給重試。
  'LINK_NO_USABLE_ACCOUNT',
  // === /accounts(POST 建立 / DELETE 解除)階段(R42.3 加 + R42.12 改名對齊)===
  'DISCOVERY_NOT_FOUND', // 404 — sessionStorage stale,需重 link
  'DISCOVERY_NOT_DISCOVERED', // 400 — anchor 已 COMPLETED;R42.11 視為「已連結過」
  'CUSTOMER_NOT_IN_DISCOVERY', // 400 — 選了不在 discovered 列表的 customer
  'MANAGER_NOT_SELECTABLE', // 400 — 選了 MCC(FE 端 checkModel="leaf" 理論不該撞)
  'ACCOUNTS_INTERNAL_FAILED', // 500 — R42.12 POST/DELETE /accounts Firestore 寫失敗
  'INTERNAL_FAILED', // 500/502 — generic 上游錯 / 寫失敗(legacy R42.3)
  'AD_ACCOUNT_NOT_FOUND', // 404 — R42.12 GET/PATCH/DELETE /accounts/{aid} 對不到
  // === OAuth 授權身分(R290 多 Google 身分綁定;BE docs/105)===
  'IDENTITY_IN_USE', // 409 — DELETE /oauth/identities/{id} 名下還有綁定帳戶;要 ?force=true
  'IDENTITY_NOT_FOUND', // 404 — 指定/解除的身分不存在或已撤銷 → 重拉 identities
  // === Meta 廣告帳戶(R299;BE docs/109)===
  // ⚠️ `FEATURE_DISABLED` 是「`meta_ads` flag 關著」—— 正常路徑**不是 toast**:整個 Meta
  // 入口要藏起來(F109-7,判準 = `features/ads/adsPlatform.isPlatformUnavailableError`);
  // 登記在這裡只給「非入口路徑撞到它」時有句人話。
  'FEATURE_DISABLED',
  'DISCOVERY_RELOAD_FAILED', // 500 — Meta 分頁端點 cache miss 後重打 Graph 失敗(重走 link 可解)
  'IDENTITY_UNLINK_FAILED', // 500 — Meta 身分解除時標記 REVOKED_BY_USER 寫失敗(可重試)
  // === sync / reports 共用(R42)===
  'OAUTH_EXPIRED',
  'SCOPE_INSUFFICIENT',
  'ACCOUNT_DISABLED',
  'RATE_LIMIT_EXCEEDED',
  'PLATFORM_TRANSIENT',
  'PLATFORM_REJECTED',
  'CONFIG_ERROR',
  'IDEMPOTENCY_CONFLICT',
  'INSUFFICIENT_SCOPE',
  'BQ_QUOTA_EXCEEDED',
  'INVALID_QUERY',
  // === sync state-invalid(R44.0 對齊 docs/35 Phase C handoff §5)===
  'NOT_CANCELLABLE', // 409 — sync cancel 對 SUCCEEDED/FAILED/CANCELLED job 撞;idempotent skip
  'SYNC_JOB_NOT_FOUND', // 404 — IDOR / 已刪除 / deep-link 過期 detail 撞
  // === sync C1 real schema(R44.1 對齊 docs/36 §0 — BE MR !29 C1 ship)===
  'CUSTOMER_NOT_IN_FRESH', // 400 — ad_account.customer_id 在 Google 端 active client set 不存在(已 revoke)
  // 422 — date_range.from > to / 超過該面上限 / format 錯。⚠️ 上限依端點族而異:
  // 報表查詢面 400(`MAX_REPORT_RANGE_DAYS`)、同步面 BE 730(FE 收在 90)——
  // 所以共用的 i18n 文案刻意不寫數字,見 `features/ads/dateRange.ts` 檔頭。
  'INVALID_DATE_RANGE',
  'SYNC_INTERNAL_FAILED', // 500 — BE 內部 Firestore down 等(對應 generic INTERNAL_FAILED 的 sync-specific 版)
  // === sync C2 worker-layer real lifecycle(R44.2 對齊 docs/37 §3)— 寫進 sync_job.error_class field 的 enum;
  //     跟 HTTP-layer error class 共存兩個 namespace(此層帶 ADS_ prefix;HTTP-layer 沒)===
  'ADS_OAUTH_EXPIRED', // Google 端 user revoke OAuth / refresh_token 過期 → FE 引導重新連結
  'ADS_DEV_TOKEN_INVALID', // BE developer_token 被 Google 撤銷 / Secret Manager 沒值 → 維運處理
  'ADS_QUOTA_EXCEEDED', // Google Ads API quota 撞 day-level 上限 → 24h reset 後 retry
  'ADS_INVALID_QUERY', // GAQL syntax 錯(BE bug)/ entity 不存在 → 維運處理
  'ADS_PARTIAL_FAILURE', // Google 回 partial success(部分 row 失敗其他成功)→ retry CTA
  'ADS_NETWORK_ERROR', // httpx.ConnectError / ReadTimeout → 自動 retry 指數退避
  'ADS_LOGIN_CUSTOMER_INVALID', // R52 對齊 docs/37 §3(2026-06-03 8th class)— BE MCC / login-customer-id 帶錯或解析錯;NOT user OAuth 失效 → opsAlert 報維運(嚴禁 reauth)
  // R297 — 廣告帳戶在 Google 端未啟用 / 已停用(BE `error_mapper.ADS_CUSTOMER_NOT_ENABLED`,
  // 由 `CUSTOMER_NOT_ENABLED` 映射)。⚠️ **不是授權問題**:重新授權沒有用,要去 Google Ads
  // 啟用帳戶 —— 所以它**不進** `needsReauth` 的判準。同一件事在帳戶 doc 上是
  // `sync_status: ACCOUNT_NOT_ENABLED`(R296),這裡是 sync job 的 `error_class`。
  'ADS_CUSTOMER_NOT_ENABLED',
  // === R301 — 授權事實改「每個綁定各自的」(BE docs/37 §3 2026-09-07)+ Meta 同步(docs/111 §4)===
  // ⚠️ 前兩個是「重新授權可解」與「重新授權**無效**」的分界:判準在 `deriveAccountActionState`
  // / `deriveConnectionState`,這裡只是已知值集(查表 fallback 仍在呈現層)。
  'NEEDS_REAUTH', // job 層:此綁定的身分(含其他工作區的)全都不能用 → 卡 `sync_status=TOKEN_EXPIRED`;重新授權可解
  'ADS_CUSTOMER_NOT_ACCESSIBLE', // 身分活著但**看不到**此帳戶(被移出 / MCC 拆連結 / Business 權限撤)→ 重授權無效,去平台後台確認存取權
  'ADS_PERMISSION_DENIED', // Meta:身分對此帳戶沒有讀成效的權限(code 10 / 200 家族)→ 重授權無效(除非 scope_missing)
  'ADS_RESOURCE_MISSING', // Meta:帳戶被刪 / 隱藏(code 100 subcode 33)→ 建議解除綁定
  // === R303 — docs/53 §A.4 2026-09-10(BE `e06972f` = `dev-update-v22-44`)===
  // **只出現在匯入 / 上傳 per-item `error_class`,不出現在同步**:授權的 Google 身分對此帳戶只有唯讀存取層級
  // (Google `authorization_error: ACTION_NOT_PERMITTED`),寫入素材被拒。**重新授權無效**(換 token 換不到更高的
  // Google Ads 權限)→ `getAdsErrorAction` 走 `access`、文案叫管理員升權;2026-09-10 前被歸成 `ADS_OAUTH_EXPIRED`
  // (beta 匯入連敗 4 次、重授權 / 重登多次無效就是這個)。
  'ADS_ACTION_NOT_PERMITTED',
  // === reports C3 endpoint(R44.3 對齊 docs/38 §6)===
  'REPORTS_INTERNAL_FAILED', // 500 — C3 reports endpoint 內部錯(BQ down / sqlglot parse 失敗)→ opsAlert
  // === R302 — 統一成效查詢 `POST /api/ads/reports/query`(BE docs/113)===
  // === R306 — docs/36 §0.A 白名單(BE `dev-update-v22-46`;`_SYNC_STATUS_BY_ERROR_CLASS`)===
  // ⚠️ **`TOKEN_TIMEOUT_EXHAUSTED` 名字裡有 TOKEN,但它不是授權問題**:BE 解 perm token 撞
  // transient 重試到上界(基礎設施),`sync_status=FAILED`,**重新授權無效**(2026-09-10 前 BE
  // 用「字串含 TOKEN」判斷 → 被寫成 `TOKEN_EXPIRED`,beta 上兩個帳戶因此被叫去白跑授權頁 = B12)。
  'TOKEN_TIMEOUT_EXHAUSTED',
  // BE 白名單裡與 sync_status 同名的那顆(refresh token 死)→ 重新授權可解。
  'TOKEN_EXPIRED',
  'MIXED_CURRENCY', // 422 — 同一列混到多種幣別(FE 固定 group_by 帶 currency_code,理論上不會遇到)
  'INVALID_CURSOR', // 422 — cursor 與查詢條件不一致(換條件要從第一頁重查)
] as const;

export type AdsErrorClass = (typeof ADS_ERROR_CLASSES)[number];

export function isKnownAdsErrorClass(s: string): s is AdsErrorClass {
  return (ADS_ERROR_CLASSES as readonly string[]).includes(s);
}

/**
 * 跨 Stage 共用 — `AssetEntity.platform_refs[]` 對應(ADR-023 A2 closed-loop)。
 *
 * **Stage 1 動工 type-level 預留**;實際 populate 由 Stage 2 publish worker
 * (ADR-023 A9 dual write transaction)在 BE 端做,FE 不寫。
 */
export interface PlatformRef {
  platform: 'google_ads';
  clientRequestId: string;
  googleResourceName: string;
  adAccountId: string;
  publishedAt: string;
}

// ===== 廣告平台(R299 Meta;BE docs/109)=====

/**
 * 我們有接的廣告平台。**wire 值就是這兩個字串**(`GET /api/ads/accounts` 的
 * `platform` 欄 / `?platform=` query / 各平台端點的路徑段 `/api/ads/{platform}/…`)。
 *
 * ⚠️ 加第三個平台不是只加一個值:每個平台都有自己的 OAuth 起點、錯誤 slug、身分狀態值
 * 與帳戶 id 顯示規則(見 `features/ads/adsPlatform.ts` 的 `Record<AdsPlatform, …>` 表 ——
 * 那些表是編譯期閘,加了值不補表 tsc 就紅)。
 */
export const ADS_PLATFORMS = ['google', 'meta'] as const;

export type AdsPlatform = (typeof ADS_PLATFORMS)[number];

export function isAdsPlatform(v: unknown): v is AdsPlatform {
  return typeof v === 'string' && (ADS_PLATFORMS as readonly string[]).includes(v);
}

// ===== OAuth 授權身分(R290 多 Google 身分綁定;R299 Meta 同形;BE docs/105 / docs/109)=====

/**
 * 一把「廣告授權身分」= 使用者授權過的一個 Google 帳號(BE `ads_oauth_tokens` 一顆 doc)。
 *
 * **登入身分與廣告授權身分解耦**:登入本系統的帳號只有一個(cuid),廣告授權可以有 N 把
 * —— A 管一批帳戶、B 管另一批。按「新增 Google 帳號授權」時跳帳號選擇器的是 **Google 那邊**
 * (`prompt=select_account`),我們的 session 全程不動、使用者不需要登出。
 *
 * `status` 刻意是 open `string` 而非封閉 union(R228 紅線:不可用 FE union 過濾 wire 值域)。
 * 顯示 gate 走 {@link KNOWN_ADS_OAUTH_IDENTITY_STATUSES};**能不能用**一律走
 * `status === 'ACTIVE'` 的正向判準(`features/ads/oauthIdentities.identityNeedsReauth`)——
 * BE 日後加第四種撤銷態時,fail-safe 方向是「當成要重新授權」,不是「當成還能用」。
 */
export interface AdsOAuthIdentity {
  /** Google `id_token.sub`(不是 email)—— link body / reauth / unlink 的鍵。 */
  oauthUserId: string;
  /**
   * 顯示用;BE 撈不到 email 時可能是空字串 —— 呈現端要有退路,不要當它一定有值。
   * ⚠️ Meta 這邊是**常態**而不是邊角:Facebook 不保證回 email(docs/109 §3)。
   */
  oauthEmail: string;
  /** R299 — 顯示名(Meta `/me` 的 name;Google 端點不回 → 空字串)。 */
  oauthName: string;
  /**
   * wire 原值:`ACTIVE` / `REVOKED_BY_GOOGLE` / `REVOKED_BY_META` / `REVOKED_BY_USER`
   * (open str)。
   */
  status: string;
  /**
   * R299 — 長期 token 到期時間(ISO)。**Meta 特有**:長期 token 約 60 天到期而且 Meta
   * 沒有 refresh 機制,到期 = 重走授權;剩 <7 天要提前提示(F109-6)。Google 端點不回
   * → `null`(Google 的 refresh token 不會到期)。
   */
  expiresAt: string | null;
  createdAt: string | null;
}

/**
 * 已知 `AdsOAuthIdentity.status` 值集(BE docs/105)。
 *
 * **只供顯示 gate**(已知 → i18n 標籤;未知 → 顯 wire 原值),不做過濾判準 —— 與
 * {@link KNOWN_DISCOVERED_ACCOUNT_STATUSES} 同慣例。
 */
export const KNOWN_ADS_OAUTH_IDENTITY_STATUSES = [
  'ACTIVE',
  'REVOKED_BY_GOOGLE',
  /** R299 — Meta 判死(code 190 家族 / 長期 token 到期)或使用者在 Facebook 端移除 App。 */
  'REVOKED_BY_META',
  'REVOKED_BY_USER',
] as const;

export type KnownAdsOAuthIdentityStatus =
  (typeof KNOWN_ADS_OAUTH_IDENTITY_STATUSES)[number];

export function isKnownAdsOAuthIdentityStatus(
  s: string,
): s is KnownAdsOAuthIdentityStatus {
  return (KNOWN_ADS_OAUTH_IDENTITY_STATUSES as readonly string[]).includes(s);
}

/**
 * `POST /oauth/link` 的 **optional** body(R290)。
 *
 * ⚠️ 不帶 = 舊行為逐位元不變(0 把 → NEEDS_AUTHORIZATION;1 把 → 直接 DISCOVERED)。
 * 只有 FE 明確要指定用哪一把身分拉樹時才帶 —— 所以 `oauthUserId` 缺值時 request 連
 * body 都不送(見 `oauthLinkRequestToDto`)。
 */
export interface OAuthLinkRequest {
  oauthUserId?: string;
}

// ===== OAuth Link(R42.1 新主 endpoint)=====

/**
 * `POST /api/ads/google/oauth/link` 回的 result(discriminated union)。
 *
 * - `DISCOVERED`:用戶已連結 + 有 adwords scope refresh token + MCC discovery 完;
 *   `discovered_accounts` 是 client account 清單 **首頁 ≤200 筆**(小樹 = 全部),
 *   其餘走 `GET /discoveries/{id}/accounts` 分頁/搜尋(R237 規模化,docs/31 §3.3)
 * - `NEEDS_AUTHORIZATION`:從未連結 / token revoked / scope insufficient;
 *   `authorization_url` 由 backend 組好(含 perm OAuth host + adwords scope),
 *   FE `window.location.replace(authorization_url)` 跳去 grant
 *
 * **同步 flow**:**沒 anchor doc 狀態機**(R42.0 設計已棄);response 同步給齊
 * accounts 列表或 authorization_url。
 */
export type OAuthLinkResult =
  | {
      state: 'DISCOVERED';
      discoveryId: string;
      /** 首頁 ≤200 筆(小樹 = 全部);完整清單走分頁 endpoint(R237)。 */
      discoveredAccounts: DiscoveredAccount[];
      /**
       * 快照總筆數(R237)。⚠️ **R238 兩階段後 = 第一層筆數**(link 只預拉
       * 根 + 直屬 `level≤1`;深層帳號不入快照,用完整 ID 搜尋時 BE live 查)——
       * 顯示要用「第一層」語意,別當成全部帳號數。
       */
      discoveredTotal: number;
      /** true ⇒ 首頁沒回完,還有更多要用分頁 endpoint 拿(R237)。 */
      hasMore: boolean;
      /**
       * 這棵樹是用哪一把授權身分拉的(R290;**顯示用**,不是授權判定)。
       *
       * BE 部署多身分之前不回這兩欄 → mapper 收斂成 `null`,呈現端**拿不到就不表態**
       * (不要 fallback 猜成「當前那把」—— 那正是 R263 `accountSyncFacts` 那族 bug)。
       */
      oauthUserId: string | null;
      oauthEmail: string | null;
      /**
       * R300(BE docs/110 P2)— **本工作區已綁定**(ACTIVE / TOKEN_EXPIRED)的 customer_id /
       * account_id,去重升冪。挑選頁拿它預勾 + 標「已連結」+ 鎖住;沒新增就不打
       * `POST /accounts`。舊 BE 缺欄 → `[]`(mapper 收斂),挑選頁退回 `GET /accounts` 自己算。
       */
      boundCustomerIds: string[];
    }
  | {
      /**
       * R290 — 名下 ≥2 把身分、而 request 沒指定用哪一把:BE 不替使用者猜(隱性預設
       * = 「帳號清單無故換了一批」的驚喜),回這個 state + 身分清單讓 FE 出選擇器,
       * 使用者選完**帶 `oauthUserId` 重打同一支 link**。
       */
      state: 'IDENTITY_REQUIRED';
      identities: AdsOAuthIdentity[];
    }
  | {
      state: 'NEEDS_AUTHORIZATION';
      /** Backend 組好的 perm OAuth URL,FE 直接 redirect。 */
      authorizationUrl: string;
      /** Wave 1 = `["https://www.googleapis.com/auth/adwords"]`;觀測用,FE 不需依此分支。 */
      requiredScopes: string[];
      /**
       * `no_oauth` 表完全沒授權過 / `insufficient_scope` 表授權過但缺 adwords /
       * `token_revoked`(R289 加;BE 2026-08-27)表存放的 refresh_token 被 Google 端
       * 撤銷或過期 / `token_expired`(R299;**Meta 特有**)表 Meta 長期 token 到期
       * —— **四者 FE 動作相同**(導 `authorizationUrl` 重走 consent)。唯一的差別是
       * 文案:`token_expired` 要講「授權已到期,請重新授權」而不是「登入」(docs/109 §3)。
       */
      reason: 'no_oauth' | 'insufficient_scope' | 'token_revoked' | 'token_expired';
    };

export interface DiscoveredAccount {
  customerId: string;
  descriptiveName: string;
  isManager: boolean;
  currencyCode: string;
  timeZone: string;
  level: number;
  parentCustomerId: string | null;
  testAccount: boolean;
  autoTaggingEnabled: boolean;
  /**
   * `customer_client.status` 的 Google enum name。BE 端是 **open `str`**(值來自
   * Google API,缺 → `UNSPECIFIED`),所以 FE 型別刻意不用封閉 union —— R228 紅線
   * 「不可用 FE union 過濾 wire 值域」:Google 加新值時封閉 union 會讓列被靜默
   * 濾掉/型別謊報。顯示層走 {@link KNOWN_DISCOVERED_ACCOUNT_STATUSES} gate:
   * 已知值 → i18n 標籤;未知值 → 顯 wire 原值不猜。
   *
   * R299 — Meta 列的值域不同:`ACTIVE` / `DISABLED` / `UNSETTLED` / … / `UNKNOWN_<n>`
   * (BE `account_status_label`)。「啟用中」的判準因此是**平台相依**的:Google 看
   * `ENABLED`、Meta 看 `ACTIVE`(見 `features/ads/adsPlatform.discoveredAccountIsActive`)。
   */
  status: string;
  /**
   * R299 — Meta 原生權限(`MANAGE` / `ADVERTISE` / `ANALYZE` / `DRAFT`)。**含 `ADVERTISE`
   * 或 `MANAGE` = 可寫**,否則唯讀(能看報表、不能上傳素材)→ picker 畫「唯讀」badge
   * (F109-4)。Google 端點不回這欄 → `null`;Meta 欄位缺也是 `null` = 未知,**不猜**。
   */
  userTasks: string[] | null;
}

/**
 * `DiscoveredAccount.status` 的已知值集(docs/31 §3.3;R237;R299 併入 Meta 值)。
 *
 * 只供**顯示層** gate(已知 → i18n 標籤 / badge;未知 → 顯 wire 原值),
 * **不做過濾判準** —— 過濾一律交 BE(`?status=` query)。
 * 非啟用中的帳號 UI 灰字 + badge,但仍可勾選(F90-6;Meta `DISABLED` 也不擋綁定,
 * 歷史成效仍可讀 —— docs/109 §5)。
 */
export const KNOWN_DISCOVERED_ACCOUNT_STATUSES = [
  // Google `customer_client.status`
  'ENABLED',
  'CANCELED',
  'SUSPENDED',
  'CLOSED',
  'UNSPECIFIED',
  // R299 — Meta `account_status` 標籤(BE `ACCOUNT_STATUS_LABELS`;`CLOSED` 兩邊同名同義)
  'ACTIVE',
  'DISABLED',
  'UNSETTLED',
  'PENDING_RISK_REVIEW',
  'PENDING_SETTLEMENT',
  'IN_GRACE_PERIOD',
  'PENDING_CLOSURE',
] as const;

export type KnownDiscoveredAccountStatus = (typeof KNOWN_DISCOVERED_ACCOUNT_STATUSES)[number];

export function isKnownDiscoveredAccountStatus(s: string): s is KnownDiscoveredAccountStatus {
  return (KNOWN_DISCOVERED_ACCOUNT_STATUSES as readonly string[]).includes(s);
}

// ===== Discovery accounts 分頁/搜尋(R237 規模化 — GET /discoveries/{id}/accounts)=====

/**
 * `GET /api/ads/google/discoveries/{discovery_id}/accounts` 的 query(docs/31 §3.3b)。
 *
 * 全部選填;`q` 支援名稱不分大小寫子字串,去連字號/空白後為純數字時同時比
 * customer_id 前綴(Google Ads UI 的 `123-456-7890` 格式整段貼上可用,FE 不必清洗)。
 * **Rate limit 60/min;搜尋輸入要 debounce ≥ 300ms**。
 */
export interface DiscoveryAccountsQuery {
  /** 搜尋關鍵字(≤100 字;空/缺 = 瀏覽模式)。 */
  q?: string;
  /** 只回該狀態(如 `ENABLED`;「只顯示啟用中」開關用)。 */
  status?: string;
  /** true = 濾掉 MCC(`is_manager=true`)。 */
  selectableOnly?: boolean;
  /**
   * 只回該節點底下的列(**含節點自身**;階層點擊展開用)。
   *
   * **R240(docs/90 §1 ads.12)**:指到 `is_manager=true` 且快照尚無子列的 sub-MCC →
   * BE **live 打 Google** 補它的直屬下一層(首次 ~0.5–1s,之後走 BE cache 即回)。
   * 回的是**直屬一層**,不含孫層 —— 孫層要對該子 manager 再打一次(可遞迴到底)。
   *
   * ⚠️ **live 失敗與「真的沒有子帳號」在 wire 上無法區分** —— BE 兩者都回 200 且
   * 只有節點自己(fail-soft,失敗不標記所以再點會重試)。顯示層文案必須兩者都成立。
   */
  parentCustomerId?: string;
  /**
   * true = 只回第一層(`parent_customer_id` 為空的根 / standalone 帳號)。
   *
   * **R240 階層瀏覽的分頁地基**:BE 的 `parent_customer_id` 展開會把 live 拿到的子列
   * **回填進同一份 pages cache**(`_cache_pages(cache_key, merged)`)→ 未過濾的 view
   * 會隨展開變長,offset 分頁就會位移(漏列 + 重複)。帶 `top_level_only=true` 的 view
   * 只含 parent 為空的列,**展開不會改變它** → offset 穩定。
   *
   * ⚠️ 搜尋模式**不可**帶 —— 深層帳號的 parent 非空,帶了會把 live 查回的深層結果濾掉。
   */
  topLevelOnly?: boolean;
  /** ≥0;預設 0。快照不可變 ⇒ offset 分頁穩定不跳頁。 */
  offset?: number;
  /** 1–200;BE 預設 50。 */
  limit?: number;
}

/**
 * `GET /discoveries/{id}/accounts` 200 result(docs/31 §3.3b;R237)。
 * R239 — `truncated` 已隨 BE 移除(docs/90 最終版 §2:兩階段第一層量小,不存在
 * 截斷概念;banner UI 同輪整段刪)。
 */
/**
 * sub-MCC 展開的 gate 結果(R240.2;docs/90 2026-08-16 增補)。
 *
 * 這是 BE 為了解掉「三種情況同形」而補上 wire 的 —— 在它之前,「真無子帳號」、
 * 「live 查 Google 失敗」、「點到的根本不是 MCC」三者都是 200 + 只回節點自己,
 * FE 只能用一句模糊文案兼顧,除錯也只能翻 BE log。
 *
 * ⚠️ **open str**:BE 之後可能加值 → FE 用已知值做**顯示 gate**,未知值走保守分支
 * (顯示可重試),不猜也不當成成功(R228/R230 慣例)。
 */
export type DiscoveryExpandStatus =
  /** live 補到子列 */
  | 'expanded'
  /** live 權威確認**真的沒有**子帳號 */
  | 'expanded_empty'
  /** 快照本來就有子列(沒打 Google) */
  | 'already_present'
  /** 先前已確認空,走 cache 不重打 */
  | 'cached_empty'
  /** ⚠️ 該列不是 MCC —— FE 本來就不該對它發展開請求(gate 沒守住的訊號) */
  | 'not_manager'
  /** id 不在樹中(異常) */
  | 'not_found'
  /** 憑證面問題(可重試) */
  | 'no_oauth'
  | 'token_unavailable'
  /** live 查詢失敗(可重試) */
  | 'unresolved'
  | 'failed';

/** 「這個狀態代表確定沒有子帳號」—— 只有 BE 給出權威答案的兩個值算數。 */
export const DISCOVERY_EXPAND_CONFIRMED_EMPTY: readonly DiscoveryExpandStatus[] = [
  'expanded_empty',
  'cached_empty',
];

export interface DiscoveryAccountsResult {
  discoveryId: string;
  /** 快照總筆數(未過濾;R238 兩階段後 = 第一層筆數)。 */
  total: number;
  /** 過濾後總筆數(顯示「共 N 筆符合」)。 */
  filteredTotal: number;
  offset: number;
  limit: number;
  hasMore: boolean;
  accounts: DiscoveredAccount[];
  /**
   * 展開 gate 結果;**只有帶 `parentCustomerId` 的請求才有值**(其餘 null)。
   * 型別是 `string` 不是 union —— BE 是 open str,未知值要顯示得出來(不可 fail-closed 成猜)。
   */
  expandStatus: string | null;
}

/**
 * 一次 link / unlink batch 的上限(R237,F90-7)。
 *
 * BE `POST /accounts` 的 `selected_customer_ids` 與 `DELETE /accounts` 的
 * `deleted_customer_ids` 都是 `max_length=200`(accounts_router.py)——超額直接
 * 422。以前帳號少很難踩到;4000+ 帳號的超級用戶會踩,UI 在勾到上限時擋 + 提示。
 */
export const ACCOUNTS_BATCH_MAX = 200;

/**
 * Link Accounts request — 用戶在 MCC 樹勾完選後,把 **新勾的** customer_ids 跟
 * `discoveryId` 一起送到 `POST /api/ads/google/accounts` 建 ad_account doc。
 *
 * **R42.12 backend pivot**(2026-05-18,backend 同步 spec):
 * - 從 `POST /discovery/{id}/finalize` 改名 `POST /accounts`;`discoveryId` 從 path
 *   移到 body
 * - Response 帶 `alreadyExisted` per account(dedupe 命中時 reuse 既有 ad_account_id 不重建)
 * - 「取消勾選」改走獨立的 `DELETE /accounts`(`BulkUnlinkRequest`),不再 mix 進 link 請求
 */
export interface LinkAccountsRequest {
  discoveryId: string;
  /** ⊆ `discoveredAccounts` 內 `isManager=false` 的 customer_id;空 list 合法 */
  selectedCustomerIds: string[];
}

export interface LinkedAdAccount {
  adAccountId: string;
  customerId: string;
  descriptiveName: string;
  isManager: boolean;
  /** true = dedupe 命中 reuse 既有 ad_account_id;false = 本次新建 */
  alreadyExisted: boolean;
  /** R299 — Meta finalize 每筆帶 `write_ok`(`false` = 唯讀帳戶);Google 不回 → `null`。 */
  writeOk: boolean | null;
  /**
   * R300(BE docs/110 P4)— true = 這筆原本綁在**已失效**的舊身分,這次改綁到本次授權身分
   * (同時 `alreadyExisted=true`)。舊 BE 缺欄 → false。呈現:「N 個帳戶已改用新授權」。
   */
  rebound: boolean;
}

export interface LinkAccountsResponse {
  adAccounts: LinkedAdAccount[];
}

/**
 * Bulk Unlink request — `/ads/accounts/select` 取消勾選 diff 算出來的 customer_ids
 * 送到 `DELETE /api/ads/google/accounts` 批次解除(soft delete + best-effort perm DELETE)。
 *
 * **R42.12 新加 endpoint**(對齊 Bryan「取消勾選送 DELETE 作為最終修改」)。
 * 上限 200 customer_ids per request。空 list → 422,FE 端先 guard 不打。
 */
export interface BulkUnlinkRequest {
  deletedCustomerIds: string[];
}

export interface BulkUnlinkedAccountRef {
  adAccountId: string;
  customerId: string;
}

export interface BulkUnlinkResponse {
  deleted: BulkUnlinkedAccountRef[];
  /** 找不到的 customer_id(idempotent skip,不算錯) */
  skippedCustomerIds: string[];
}

// ===== Ad accounts(docs/31 §3.3 / §5.4)=====

/**
 * 帳戶同步狀態的**已知值集**(docs/31 §5.4 + docs/36 錯誤對照表)。
 *
 * ⚠️⚠️ **BE 端是開放值域,這個 union 不是它的全集** —— 2026-08-31 BE 加了
 * `ACCOUNT_NOT_ENABLED`(Google 端未啟用帳戶),只寫進 docs/36 的錯誤對照表、
 * docs/31 的型別註解沒補(那才是 FE 照抄型別的地方)→ FE 的 union 停在 5 個值,
 * 而 dev 一綁到未啟用帳戶就**整頁白屏**(`SYNC_STATUS_PALETTE[status].bg` →
 * `undefined.bg` → TypeError)。
 *
 * ⚠️ **TS 型別救不了這一族**:型別是編譯期的,API 真的送來一個 union 裡沒有的字串時
 * `Record<AccountSyncStatus, X>` 一樣回 `undefined`。能救的只有**查表給 fallback**
 * (未知值 → 中性樣式 + 顯 wire 原值),見 `AdsAccountsPage.SyncStatusBadge`。
 *
 * 所以 wire 欄位一律型別 `string`(見 {@link AdAccountSummary.syncStatus}),這個
 * union **只供顯示層 gate**:已知 → i18n 標籤 + 配色;未知 → 中性 + 原值。
 * 同 {@link DiscoveredAccount.status} 的處理(R228 紅線「不可用 FE union 過濾 wire 值域」)。
 */
export const ACCOUNT_SYNC_STATUSES = [
  'NEVER',
  'RUNNING',
  'OK',
  'FAILED',
  'TOKEN_EXPIRED',
  /** Google 端未啟用帳戶(BE 2026-08-31;同步會被標 FAILED,重新授權**沒有用**)。 */
  'ACCOUNT_NOT_ENABLED',
] as const;

export type AccountSyncStatus = (typeof ACCOUNT_SYNC_STATUSES)[number];

/**
 * wire 字串是不是我們認得的同步狀態。
 *
 * ⚠️ 用途是**顯示層 gate**,不是過濾器 —— 未知值一律照顯(顯 wire 原值),
 * 不可以拿它把列濾掉或當成某個已知值(R228 紅線)。
 */
export function isAccountSyncStatus(v: unknown): v is AccountSyncStatus {
  return typeof v === 'string' && (ACCOUNT_SYNC_STATUSES as readonly string[]).includes(v);
}

/**
 * **R42.12 對齊 2026-05-18 BE spec §4.1 `AdAccountOut`**:
 * - 砍 `firestorePaths`(BE summary 沒給 listener path)
 * - 新加 `status`(`ACTIVE` | `TOKEN_EXPIRED`,REVOKED 已 BE 過濾掉)
 * - 新加 `discoveredViaAnchorId` / `createdAt` / `updatedAt`
 * - `currencyCode` / `timeZone` / `syncStatus` / `lastErrorClass` 改 nullable
 * - Detail = Summary 同 shape(BE spec §4.2;砍 loginCustomerId / grantedScopes /
 *   publishConsentAt / revokedAt 既有 R42.0 設計)
 */
export interface AdAccountSummary {
  /**
   * R299 — 這顆帳戶屬於哪個平台(`GET /api/ads/accounts` 的 `platform` 欄;單平台端點
   * 2026-08-31 起也帶)。**寫操作要照它分流**(unlink / PATCH 走各平台自己的端點),
   * 顯示也靠它(品牌圖示、帳戶 ID 格式、可不可以同步)。舊 BE 缺欄 → mapper 以呼叫的
   * 端點補(單平台端點知道自己是誰),不猜。
   */
  platform: AdsPlatform;
  adAccountId: string;
  /**
   * Google:`customer_id`(10 位數,UI 慣例 `xxx-xxx-xxxx`);Meta:FB `account_id`
   * 純數字(**不含 `act_`,也不可套 Google 的連字號格式** —— F109-9)。
   * 顯示走 `features/ads/adsPlatform.formatCustomerId`。
   */
  customerId: string;
  descriptiveName: string;
  isManager: boolean;
  currencyCode: string | null;
  timeZone: string | null;
  syncEnabled: boolean;
  /**
   * wire `sync_status`。**刻意是 `string` 不是 {@link AccountSyncStatus}** ——
   * BE 端值域開放(2026-08-31 加 `ACCOUNT_NOT_ENABLED` 沒同步型別註解就是實例),
   * 封閉 union 會讓 FE 型別謊報而 runtime 照樣拿到新字串。已知值集只在顯示層 gate。
   */
  syncStatus: string | null;
  lastSyncAt: string | null;
  lastErrorClass: string | null;
  /**
   * 連結層狀態:`ACTIVE` / `TOKEN_EXPIRED`;Meta 端點還會回 `REVOKED`(docs/109 §5)。
   *
   * R299 起是 **open `string`**(BE 兩個平台的 response model 都宣告 `str`),判準一律
   * **正向** `=== 'ACTIVE'`(`syncViewModel.deriveAccountActionState`):未知值當成
   * 「需重新授權」,fail-safe 方向不是「當成還能用」(同 `identityNeedsReauth`)。
   */
  status: string;
  discoveredViaAnchorId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /**
   * R290 — 這個帳戶是靠哪一把授權身分綁進來的(「綁定來源」badge)。
   *
   * `null` = 拿不到:BE 未部署多身分(缺欄)、或存量 doc 還沒被 worker lazy 回填
   * (wire 回空字串)。兩種都**不畫 badge** —— 猜一個 email 顯示比不顯示更糟。
   * ⚠️ Meta 列空字串是常態(Facebook 不保證回 email),不是資料壞了。
   */
  oauthEmail: string | null;
  /**
   * R301.1 — 綁定這顆帳戶的身分 id(BE 2026-09-08 additive,`83569dc`;= 各平台
   * `GET /oauth/identities` 的 `oauth_user_id`)。直達重授權 `/oauth/start?reauth=<id>` 用它,
   * **不再靠 `oauth_email` 對身分**(Meta 常為空)。legacy 未回填 = `""` → 這裡 `null` → 退連結頁。
   */
  oauthUserId: string | null;
  /**
   * R299 — Meta 原生權限(`MANAGE` / `ADVERTISE` / `ANALYZE` / `DRAFT`);Google 列恆 `null`。
   * 判「可不可以寫」請用 {@link AdAccountSummary.writeOk}(BE 已推導),不要自己重算。
   */
  userTasks: string[] | null;
  /**
   * R299 — `false` = **唯讀帳戶**(缺 ADVERTISE):可看報表、不能上傳素材 → 畫「唯讀」
   * badge、藏「上傳到 Meta」入口(F109-5);`true` = 可寫;`null` = 未知(Google 列、
   * 或 Meta 欄位缺)—— 未知**不畫**唯讀 badge,也不禁用入口(BE 才是最後一道)。
   */
  writeOk: boolean | null;
  /**
   * R308(BE docs/36 2026-09-10 additive)— `writeOk` 是**什麼時候探到的**(ISO-8601;缺欄 / 從沒探過 → null)。
   * 用途:使用者去平台後台升權後回來,看到同一個 `false` 才分得出「還沒重探」與「升權沒生效」。
   */
  capabilityProbedAt: string | null;
  /** R308 — doc 現存的存取層級;**BE 目前一律 null**(欄位先開)。 */
  accessRole: string | null;
  /**
   * R299 — Meta 帳戶在平台端的狀態標籤(`ACTIVE` / `DISABLED` / `UNSETTLED` / …);
   * `DISABLED` **不擋綁定**(歷史成效仍可讀),只提示。Google 列恆 `null`
   * (Google 的同一件事在 `syncStatus === 'ACCOUNT_NOT_ENABLED'`)。open str,顯示層 gate。
   */
  accountStatus: string | null;
}

/** R42.12 — detail = summary(BE spec §4.2)。type alias 保留語意分群。 */
export type AdAccountDetail = AdAccountSummary;

/** PATCH body — `sync_enabled` required(BE `extra="forbid"` 鎖死)。 */
export interface AdAccountPatchRequest {
  syncEnabled: boolean;
}

/**
 * R299 — `GET /api/ads/accounts[?platform=…]` 跨平台總覽(docs/109 §6)。
 *
 * `platforms` = **本次實際涵蓋**的平台:`meta_ads` flag 關著時 BE 會把 `meta` 拿掉
 * (顯式帶 `?platform=meta` 也只回空,不 403)→ FE 據此決定「Meta 入口顯不顯」(F109-8)
 * —— 「沒回 Meta 列」與「沒有 Meta 帳戶」在 wire 上就是靠這欄分開的,別拿 `accounts`
 * 裡有沒有 meta 列去猜。排序 `created_at` 升冪、不分頁、唯讀。
 */
/**
 * R308 — 重探寫入能力的結果(`POST /api/ads/google/accounts/{aid}/capability/reprobe`)。
 *
 * ⚠️ **`probedAt === null` 是「根本沒探到」不是「唯讀」**(BE 原話:這次連 token / client 都沒解出來,
 * 後端**不覆寫**先前存的值)→ 呈現端要說「暫時無法確認,請稍後再試」,**不可以**把它當成 `writeOk === false`。
 */
export interface AccountCapability {
  writeOk: boolean | null;
  accessRole: string | null;
  probedAt: string | null;
}

export interface AdAccountsOverview {
  platforms: AdsPlatform[];
  accounts: AdAccountSummary[];
}

// ===== Sync jobs(docs/31 §3.3 / §4.2 + §5.5)=====

/**
 * **R44.1 對齊 docs/36 §2**:`PARTIAL_FAILURE` BE 拍砍,partial completion 用
 * `status === 'FAILED' && subtasksDone > 0` 派生(docs/35 §3.4 mock 已 implicitly
 * 用此邏輯)。FE UI 維持「部分成功」黃 badge 視覺 — Panel 上輪共識「Option B
 * FE 派生維 UX 防禦」(50 account 同步 47 成功 3 失敗,user 仍看得到部分結果)。
 */
export type SyncJobStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED';

export const SYNC_JOB_STATUSES: readonly SyncJobStatus[] = [
  'PENDING',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
];

/**
 * Partial completion 派生 — `status === 'FAILED' && subtasksDone > 0`。
 *
 * **R44.1 取代 `PARTIAL_FAILURE` enum**(BE 拍砍):docs/36 §2 status 5 個沒
 * `PARTIAL_FAILURE`;docs/35 §3.4 mock 描述 partial 用 `status="FAILED" +
 * subtasks_done > 0`。UI 端用此 helper 派生「部分成功」黃 badge,enum 砍但
 * UX 防禦不破(R42 既有「failed 47/50」視覺維持)。
 */
export function isSyncJobPartialFailure(
  detail: Pick<SyncJobDetail, 'status' | 'subtasksDone'>,
): boolean {
  return detail.status === 'FAILED' && detail.subtasksDone > 0;
}

export type SyncJobPhase = 'FETCH' | 'DEDUPE' | 'DONE';

export const SYNC_JOB_PHASES: readonly SyncJobPhase[] = ['FETCH', 'DEDUPE', 'DONE'];

export type SyncMode = 'ENTITY' | 'METRICS' | 'BOTH';

export const SYNC_MODES: readonly SyncMode[] = ['ENTITY', 'METRICS', 'BOTH'];

/** Terminal job state — listener 收到後可 unsub。 */
export function isSyncJobTerminal(s: SyncJobStatus): boolean {
  return s === 'SUCCEEDED' || s === 'FAILED' || s === 'CANCELLED';
}

export interface SyncSubmitRequest {
  adAccountIds: string[];
  mode?: SyncMode;
  dateRange?: { from: string; to: string };
}

/**
 * `POST /api/ads/google/sync` 202 response 元素(per-account 一個 sync_job)。
 *
 * **R44.1 對齊 docs/36 §1**:加 `customerId`(BE 真實 response 帶,FE debug 可用)。
 * **R227**:`firestorePaths` 退場 — BE 從未 ship(見 `SyncJobSubmitInfoDto` 註解)。
 */
export interface SyncSubmitJobInfo {
  syncJobId: string;
  adAccountId: string;
  customerId: string;
  totalSubtasks: number;
}

export interface SyncSubmitResult {
  syncJobs: SyncSubmitJobInfo[];
}

/**
 * `POST /api/ads/google/sync/jobs/{jid}/cancel` 200 response。
 *
 * **R44.1 對齊 docs/36 §4**:R42 既有 cancelSync `Promise<void>`(直接 await),
 * docs/36 BE 真實 return `{ sync_job_id, status }`。Idempotent — 已 `CANCELLED`
 * 再 cancel 仍 200(不會 409 NOT_CANCELLABLE);只有非終態 → 終態才會 emit。
 */
export interface CancelSyncResponse {
  syncJobId: string;
  status: SyncJobStatus;
}

/**
 * Sync job detail — 對齊 docs/36 §2 真實 wire format。
 *
 * **R44.1 重大變更**(對齊 BE MR !29 C1 ship spec):
 * - `adAccountIds: string[]` → `adAccountId: string` + 加 `customerId`(per-account
 *   一個 sync_job;R42 假設「多 ad_account 一個 sync_job」修正)
 * - 加 `createdAt` / `updatedAt`(BE Firestore doc 標準欄)
 * - rename `completedAt` → `finishedAt`(docs/36 wire format)
 * - **砍** `phase1DoneAt` / `phase2DoneAt` / `errorMessage` / `triggeredBy`
 *   (R42 預留但 BE 真實 spec 不 ship)
 *
 * **R227**:`firestorePaths` 退場 — BE 從未在任何 sync 端點 ship 過這欄,docs/31 §4
 * listener 模型 superseded,進度改 polling(docs/37 §4;見 `useSyncJob`)。domain 欄名
 * `totalSubtasks` 不動(R42 慣例),wire 欄名見 `SyncJobDetailDto.subtasks_total`。
 */
export interface SyncJobDetail {
  syncJobId: string;
  adAccountId: string;
  customerId: string;
  mode: SyncMode;
  dateRange: { from: string; to: string } | null;

  status: SyncJobStatus;
  phase: SyncJobPhase;

  totalSubtasks: number;
  subtasksDone: number;
  subtasksFailed: number;
  subtasksSplit: number;

  progressHint: string | null;
  errorClass: AdsErrorClass | null;
  traceId: string | null;

  createdAt: string | null;
  updatedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface SyncJobsListRequest {
  adAccountId?: string;
  status?: SyncJobStatus;
  limit?: number;
  cursor?: string;
}

export interface SyncJobsListResponse {
  syncJobs: SyncJobDetail[];
  nextCursor: string | null;
}

// ===== Reports(docs/31 §3.3 / §5.6 + §6.5)=====

/**
 * Reports query metric — 對齊 BE BQ allowlist enum(docs/38 §1)。
 *
 * **R44.3 重大變更**(對齊 docs/38 §1 + §4):
 * - `cost_amount` → `cost_micros`(BE allowlist 用 BQ table column 真實 name);
 *   BE 收到後內部處理,**response 帶 `cost_amount`(已 ÷ 1M)+ `currency_code`**
 * - 加 `average_cpc_micros`(同 micros 慣例;response 端應該也會 ÷ 1M but 對齊 docs/38 §1 wire 用 micros)
 *
 * **FE 慣例**:
 * - **Request 端**:metrics 陣列傳 `cost_micros` / `average_cpc_micros`(對齊 BE allowlist)
 * - **Response 端**:row 拿到 `cost_amount: number`(已 ÷ 1M)+ `currency_code` 同行(對齊 `ReportRow` 型別 + AdsReportsPage 既有 render)
 */
export const REPORT_METRICS = [
  'impressions',
  'clicks',
  'cost_micros',
  'conversions',
  // WA 開欄:轉換價值(ROAS = conversions_value ÷ cost_amount 由 FE 派生)
  'conversions_value',
  'video_views', // R52 docs/38 §4.1:BE 底層查 video_trueview_views,僅計 TrueView(skippable in-stream / in-feed / Shorts)計費觀看,不含 bumper / 非 skippable;enum 名與 wire 不變。VTR KPI 目前仍 mock(AdsReportsPage L495),真實對接時再補 UI tooltip
  'ctr',
  'average_cpc_micros',
  // ── 影片 rate 欄(BE `reports_builder._METRIC_TO_SQL`;各 grain 內 AVG)──
  // ⚠️ 值域是 **0–1 的比率**(BQ `video_trueview_view_rate` 等原欄),不是百分比。
  'video_view_rate',
  'video_completion_rate', // = BQ video_quartile_p100_rate(VCR)
  'video_quartile_p25_rate',
  'video_quartile_p50_rate',
  'video_quartile_p75_rate',
] as const;

/**
 * R230 起 union 由 `as const` tuple 派生(仿 `ADS_ERROR_CLASSES` 慣例)——
 * runtime tuple 是為了讓契約鎖(`beContract.test.ts`)能拿 FE 值集直接對 BE
 * `reports_models.ReportMetric` Literal,漂移任一方向都紅。
 */
export type ReportMetric = (typeof REPORT_METRICS)[number];

export const REPORT_GROUP_BYS = [
  'data_date',
  'customer_id',
  'campaign_id',
  'ad_group_id',
  'ad_group_ad_id',
  'segment_device',
  // 素材維度(T9 closed-loop)。**⚠️ 目前恆為 NULL** —— BE ingestion
  // (`row_transform._common_flat`)把 `our_asset_id` 寫死 `None`,Wave 3a 反查回填
  // 尚未實作,所以拿它 group 只會得到一列 key=null 的總計。呼叫端要濾掉 null;
  // BE 回填後同一條路自動有資料。與下面兩個 Google 原生 asset 維度**不可混用**(422)。
  'our_asset_id',
  // ── 素材成效(Google 原生 asset-grain;2026-08-12 S2,docs/38 §2b)──
  // 出現任一即為素材成效查詢:BE 改查 `google_asset_metrics_clean` 並鎖單一
  // `asset_link_scope`;response envelope 帶 `aggregation_caveat`(非可加標記)。
  'asset_id',
  'asset_field_type',
  /**
   * 素材歸組鍵(G1-G2;BE `fb78aeb`,docs/89 §3)。同文案 / 同影片跨 campaign 自動
   * 合成一組:`yt:`(同影片)/ `txt:`(同文案,已正規化)/ `name:`(同名,弱鍵)。
   * 單獨 group_by 它 = **組聚合檢視**(每組一列,附 `asset_group_name`)。
   * ⚠️ **值不可持久化** —— BE 明示圖片同圖強鍵(hash)上線後鍵值會變。
   */
  'asset_group_key',
  /**
   * **相似**歸組鍵(計劃書④,docs/89 §3;BE `92bbb7b`)—— 微改也合(標點、
   * `預定`/`預訂`、`Map`/`Maps`),看的是「這個創意概念」的總量。
   *
   * ⚠️ 鍵值**依 `filters.similarity_level` 切段**(前 2/4/8 段)→ 換鬆緊檔位
   * **必須重新查詢**,不能拿同一批列在前端重算。
   */
  'asset_similar_key',
  /**
   * 掛載層維度(計劃書⑧,docs/38 2026-08-16 第二批;BE `568fc7d`)——
   * **帶了就進「多層模式」**:三層同表分列,每列多 `asset_link_scope`(層;
   * **null = 該期間未投放**)+ `platform`(恆 `google`)兩欄。
   *
   * ⚠️ 三個綁在一起的規則(缺一就 422):
   *   1. 要送 `filters.assetLinkScopes` chips → group_by **必須**含這個值
   *   2. 多層模式下 `filters.assetLinkScope`(舊單值)**不可同時帶**
   *   3. group_by 同時含 `ad_group_ad_id` 時,chips 必須恰為 `['ad']`
   *      (link_id 跨層語意不同 —— ad 層是 ad_group_ad、campaign_link 層是 campaign)
   */
  'asset_link_scope',
] as const;

export type ReportGroupBy = (typeof REPORT_GROUP_BYS)[number];

/**
 * **Meta 面專屬**的 group_by 值(docs/116 §1;Meta 的層名:`adset` = Google `ad_group`,
 * `ad_id` = 逐廣告)。⚠️ Google 面帶它們是 422(F116-8 反向亦然:Meta 面帶
 * `ad_group_id` / `ad_group_ad_id` 也是 422)—— request 型別放寬成聯集只是因為三支
 * 端點共用這份 request(同上面 `metrics` 欄的既有判準),**每個呼叫點要自己帶對面的值**。
 */
export const META_ONLY_GROUP_BYS = ['adset_id', 'ad_id'] as const;

export type MetaOnlyGroupBy = (typeof META_ONLY_GROUP_BYS)[number];

/** 查詢 request 能出現的 group_by 全集(Google 面值集 + Meta 面專屬值)。 */
export type ReportRequestGroupBy = ReportGroupBy | MetaOnlyGroupBy;

/**
 * 相似合併的鬆緊(docs/89 §3;BE `SimilarityLevel`,預設 `normal`)。
 *
 * dev 109 顆文字素材實測:`loose` 合最多(69 組,**可能誤合**用詞重疊但語意不同的)/
 * `normal` 75 組(抽樣零誤合)/ `strict` 78 組(近似精確)。
 *
 * **檔位是嵌套的**:切鬆時組只會**併入**,不會出現「本來同組的變不同組」的跳動 →
 * 做成三段切換不會讓使用者困惑。
 */
export const SIMILARITY_LEVELS = ['loose', 'normal', 'strict'] as const;

export type SimilarityLevel = (typeof SIMILARITY_LEVELS)[number];

export type ReportDevice = 'DESKTOP' | 'MOBILE' | 'TABLET' | 'CONNECTED_TV' | 'OTHER';

/**
 * 素材成效查詢的掛載層 scope(docs/38 §2b;BE `reports_models.AssetLinkScope`)。
 *
 * 一次查詢**永遠鎖單一 scope**(BE 強制 → 對映 clean 表單一 `entity_kind`,防跨
 * view 重複加總);要多 scope 就發多次請求。
 * - `ad`(預設):RSA / App / Demand Gen 的 ad 內素材(⚠️ RDA 無資料 — Google API 限制)
 * - `campaign_link`:sitelink / callout 等 extension 掛載
 * - `pmax`:PMax asset group
 * - `campaign_agg` / `channel_agg`:帳戶聚合視角 — **資料未開通前恆空列,先別用**
 */
export const ASSET_LINK_SCOPES = [
  'ad',
  'campaign_link',
  'pmax',
  'campaign_agg',
  'channel_agg',
] as const;

export type AssetLinkScope = (typeof ASSET_LINK_SCOPES)[number];

/**
 * 掛載層 **chips 篩選**的值域(BE `AssetLinkScopeFilter`;計劃書⑧,R243)。
 *
 * = {@link ASSET_LINK_SCOPES} + `'none'`。**兩把值域刻意分開**:
 * `filters.assetLinkScope`(舊單值)收不了 `'none'`,送進去是 422;而 chips 要能單獨
 * 篩「這期間沒投放的素材」,所以多這一個值。
 *
 * `'none'` 的語意由資料決定:**該素材在查詢區間內沒有任何投放紀錄**(層為 NULL)。
 * ⚠️ **不等於「沒掛載」** —— 維度表只有素材列、沒有結構掛載列,資料證明不了掛載與否。
 * 另外它會隨 chips 選集改變意思:選了 `['ad','none']` 時,只在 PMax 投放的素材也會
 * 落進「未投放」(因為 JOIN 只允許 ad 層配對)—— 那正是舊「廣告內素材」頁籤的語意。
 */
export const ASSET_LINK_SCOPE_FILTERS = [
  ...ASSET_LINK_SCOPES,
  'none',
] as const;

export type AssetLinkScopeFilter = (typeof ASSET_LINK_SCOPE_FILTERS)[number];

/**
 * 素材成效面(asset 查詢)的 metrics 允許集 — 鏡射 BE `_ASSET_QUERY_ALLOWED_METRICS`。
 *
 * quartile 三檔與 `video_completion_rate` 素材面**不支援**(來源表無 quartile 欄,
 * docs/38 §2b)→ 帶了整支 422。契約鎖對雙方值集有斷言;素材成效表送的 metrics
 * 必須 ⊆ 這個集合。
 */
export const ASSET_QUERY_ALLOWED_METRICS: readonly ReportMetric[] = [
  'impressions',
  'clicks',
  'cost_micros',
  'conversions',
  'conversions_value',
  'video_views',
  'ctr',
  'average_cpc_micros',
  'video_view_rate',
];

/**
 * 媒體類型 chips 的值域(BE `AssetMediaType`;docs/38 2026-08-17)。
 *
 * **分類由 BE 做**(對 Google 原生 `asset.type_`),FE 只送選了哪幾類:
 * `image` = IMAGE / `video` = YOUTUBE_VIDEO / `text` = TEXT /
 * `extension` = CALLOUT・SITELINK・STRUCTURED_SNIPPET・PROMOTION・CALL・
 * CALL_TO_ACTION・DEMAND_GEN_CAROUSEL_CARD(**額外資訊**,以前被 FE 併進「文字」→
 * 「文字 52 筆」比純文字 41 筆多出來的成因)。
 *
 * ⚠️ **不要再在前端依 `asset_type` 自己分類過濾** —— 分頁之後只篩得到當頁,
 * 跨頁的同類素材看不到,`total_rows` 也對不上(docs/38 2026-08-17 原則:
 * 任何會影響「有哪些列」的條件都送後端)。
 */
export const ASSET_MEDIA_TYPES = ['image', 'video', 'text', 'extension'] as const;

export type AssetMediaType = (typeof ASSET_MEDIA_TYPES)[number];

export interface ReportFilters {
  /**
   * R326 — 媒體篩選(**統一面 `POST /api/ads/reports/query` 專屬**;docs/113
   * `UnifiedReportFilters.platforms`,不帶 / 空 = 兩平台都查)。
   *
   * ⚠️ **Google / Meta 單平台面的 request filters 沒有這一欄且 `extra="forbid"`** ——
   * 帶過去是**整支 422**(整張表讀取失敗)。所以這欄只准統一面的呼叫點
   * (`AdsReportsPage.fetchDaily`)設,**絕不可放進頁面共用 filters**(那份會被
   * 素材成效表 spread 進 Google 面查詢);有負向鎖守著。
   */
  platforms?: AdsPlatform[];
  adAccountIds?: string[];
  customerIds?: string[];
  campaignIds?: string[];
  segmentDevice?: ReportDevice[];
  /** Stage 3 reach-for-future:per-asset performance filter(ADR-023 A4)。 */
  ourAssetId?: string;
  /** 素材成效:Google 原生 asset id 篩選(≤200;出現即視為素材成效查詢)。 */
  assetIds?: string[];
  /**
   * 素材成效:掛載層 scope **單值**(缺省 BE 視為 `'ad'`;**只在素材成效查詢時可帶**,
   * 否則 422)。語意 = 鎖單一層 + 全素材補零(舊三頁籤模型)。
   *
   * ⚠️ **素材成效表已改用 {@link assetLinkScopes} chips**(R243):兩者**不可同時帶**
   * (BE 422,`reports_models.py:242`)。這一欄留著是因為 **`GET /reports/aei` 仍然
   * 單層鎖**(`aei_router.py:60`)—— AEI 那支就是靠它逐層查。BE 說舊欄行為不變、
   * 遷移完成後另一版才移除。
   */
  assetLinkScope?: AssetLinkScope;
  /**
   * 素材成效:掛載層 **chips 複選**(計劃書⑧,R243)。**不帶 = 全部層一次回**(含
   * 未投放列);`'none'` = 未投放。
   *
   * ⚠️ 送這個就**必須**在 `groupBy` 帶 `'asset_link_scope'`(BE 422:篩了層卻不把層
   * 列出來 = 回一堆看不出屬於哪層的列),而且**不可同時帶** {@link assetLinkScope}。
   * ⚠️ 改 chips = 查詢形狀變 → 舊 `cursor` 一定要丟掉從第一頁重查(否則 422
   * `INVALID_CURSOR`)。
   */
  assetLinkScopes?: AssetLinkScopeFilter[];
  /**
   * 相似合併的鬆緊(缺省 BE 視為 `'normal'`)。
   *
   * **只在 `groupBy` 含 `asset_similar_key` 時有作用** —— 帶了但沒用相似 group_by,
   * BE **直接忽略不報錯**(docs/89 §3),所以呼叫端可以固定帶著送。
   */
  similarityLevel?: SimilarityLevel;
  /**
   * 素材搜尋(R241;docs/38 2026-08-16)。1–100 字、**contains**(不是前綴)、
   * case-insensitive,比對範圍 = 素材名稱 + 素材文字(與列表顯示的 `asset_text` 同源)
   * + YT 影片標題。`%`/`_` 當字面值(參數綁定,無注入面)。
   *
   * ⚠️ **限制**:`group_by` 必含 `asset_id` / `asset_group_key` / `asset_similar_key`
   * 任一,否則 422;**空白字串也是 422** → FE 只在 trim 後非空時才帶。
   * ⚠️ 改搜尋字 = 查詢形狀變 → **舊 cursor 一定要丟掉從第一頁重查**。
   */
  assetNameQuery?: string;
  /**
   * 媒體類型 chips(R246;**後端篩**)。不帶 = 全部;需 `groupBy` 含 asset 維度鍵
   * (`asset_id` / `asset_group_key` / `asset_similar_key`),否則 422。
   */
  assetMediaTypes?: AssetMediaType[];
  /**
   * 只回「這段期間有投放」的列(`impressions > 0`;R246,**後端篩** HAVING)。
   *
   * ⚠️ **不要在前端濾掉全 0 列** —— 每頁濾完剩幾筆不定、`totalRows` 也會對不上
   * (docs/38 2026-08-17)。省略 = `false` = 零列語意(帳戶全部素材都出列)。
   */
  onlyWithDelivery?: boolean;
  /**
   * **一列給齊四種廣告模式**(docs/93;BE 2026-08-18)。開了之後每列多
   * {@link ReportRow.by_scope}(固定 4 筆)+ `primary_scope`,而**平面 metrics 欄
   * 改成「主層」的數字** —— 永遠出自單一層,結構上不可能跨層加總,`orderBy` 照舊。
   *
   * 取代「四層各打一支查詢再自己拼」的舊做法:`totalRows` / `nextCursor` 從此是
   * 單一來源。
   *
   * ⚠️ **三者互斥**(BE 422):不可與 `groupBy` 的 `'asset_link_scope'`(每層一列)
   * 或 {@link assetLinkScope}(舊單值鎖層)同時送 —— 它們是同一件事的三種形狀。
   * {@link assetLinkScopes} chips **可以**共存,語意退化成純粹的「哪些列要出現」。
   */
  includeScopeBreakdown?: boolean;
}

/**
 * 一列裡某一個廣告模式的數字(docs/93 §A;`by_scope` 的元素)。
 *
 * ⚠️ **`present` 才是「有沒有投放」的判準,不可以用「數字是不是 0」推** ——
 * 投了但零曝光與根本沒投是兩件事,只有這個欄位分得出來。
 * ⚠️ **四筆之間絕對不可相加**:同一次曝光會同時記在多層(docs/38 §6.1 非可加性)。
 */
export interface ReportScopeBreakdown {
  scope: AssetLinkScopeFilter;
  present: boolean;
  /** request 的 metrics 逐欄同名;rate 家族「沒投放」時是 `null` 不是 0。 */
  [metric: string]: string | number | boolean | null | undefined;
}

/**
 * Meta 素材縮圖的一筆換發結果(R342;BE docs/114 §8)。
 *
 * ⚠️ **兩種 source 的可靠度不同**,呈現端一定要分:`mirror` 是我方 GCS signed URL
 * (15 分鐘、恆可載入);`cdn` 是 Meta 原址,**隨時可能輪替失效** → 破圖要退佔位,
 * 不可以顯示成「這個素材沒有圖」。
 * ⚠️ signed URL **不要快取超過 15 分鐘**(BE 明示);過期重打端點即可。
 */
export interface MetaAssetThumbnail {
  assetId: string;
  url: string;
  /** ISO-8601;`null` = `cdn` 來源(Meta 不揭露輪替時間)。 */
  expiresAt: string | null;
  /** 開放字串:已知 `mirror` / `cdn`。 */
  source: string;
}

export interface ReportQueryRequest {
  /**
   * ⚠️ **值域依「打哪一面」而不同**(R342):
   *   - Google 專屬面 `POST /ads/google/reports/query` → {@link ReportMetric}(13 個)
   *   - 統一面 / **Meta 專屬面** → {@link UnifiedReportMetric} 那一族
   *     (Meta 的 `MetaReportMetric` 是 45 個,涵蓋統一面全部 + `all_conversions` 等)
   *
   * 型別放寬成聯集是因為三支端點共用這份 request;**送錯面 BE 會 422**,所以每個呼叫點
   * 要自己帶對清單(素材面的兩份是 `MAT_ASSET_METRICS` / `MAT_ASSET_METRICS_META`)。
   * ⚠️ Google 素材面**不支援 quartile 四檔**(送了整支 422),Meta 素材面支援 —— 這正是
   * 兩份清單不能合一的原因。
   */
  metrics: Array<ReportMetric | UnifiedReportMetric>;
  /**
   * 同 `metrics` 的判準:三支端點共用 → 型別是**兩面聯集**({@link ReportRequestGroupBy}),
   * 各端點各有允許子集(Google 面 = {@link REPORT_GROUP_BYS};Meta 面另含
   * `adset_id` / `ad_id` 而**不含** `ad_group_id` / `ad_group_ad_id` 等),送錯面 422。
   */
  groupBy: ReportRequestGroupBy[];
  dateRange: { from: string; to: string };
  filters?: ReportFilters;
  orderBy?: Array<{ field: string; direction: 'ASC' | 'DESC' }>;
  limit?: number;
  cursor?: string;
}

export interface ReportRow {
  is_provisional: boolean;
  /**
   * 已知值:`recent_2d_pending_settle` / `google_attribution_backfill_window`(Google;docs/38 §3)/
   * `attribution_backfill_window`(Meta;docs/113 §0-6)。**開放字串**(R296 紅線:wire 值域是開放的,
   * 封閉 union 是型別謊報);FE 目前只看 `is_provisional`,reason 不分支。
   */
  provisional_reason: string | null;
  cost_amount?: number;
  currency_code?: string;
  /**
   * 四種廣告模式各一筆(`filters.includeScopeBreakdown` 開了才有;順序固定
   * ad / campaign_link / pmax / none)。解析走 `reportsMatSource.rowScopeBreakdown`
   * —— 型別上是 `unknown`,因為這是 wire 直出的資料,不該有人直接當它是好的。
   */
  by_scope?: ReportScopeBreakdown[];
  /** 平面 metrics 出自哪一層;`null` = 完全未投放。 */
  primary_scope?: string | null;
  [key: string]: string | number | boolean | null | undefined | ReportScopeBreakdown[];
}

export interface ReportSchemaColumn {
  name: string;
  type: 'STRING' | 'INT64' | 'FLOAT64' | 'DATE' | 'BOOLEAN';
  unit?: 'micros' | 'percent' | null;
  format?: 'currency' | 'iso_date' | null;
  /** R302(docs/113)— 平台原生加總 = provider;查詢時公式 = derived;維度 / 系統欄 null。 */
  source?: 'provider' | 'derived' | null;
  /** R302(docs/113)— 這欄哪些平台有值;Meta 專屬欄 Google 列一律 null(不是 0)。 */
  platforms?: AdsPlatform[] | null;
}

// ===== R302 — 統一成效查詢(Google + Meta;BE docs/113)=====
//
// 與 Google 面同骨架、同 response envelope(`ReportQueryResponse`);差異只有:
//   - `filters.platforms` 不帶 = 兩平台都查;`group_by` 多 `platform` / `currency_code`
//   - metrics 是 docs/113 附錄 A 的 38 欄(共同 + Meta 專屬;Meta 專屬欄 Google 列 null)
//   - 沒有 asset 維度(素材成效跨平台 = 06 OQ-N5,M 波段不做)
// ⚠️ 值域一樣是開放的:BE 加欄 FE 不認就是不畫(`schema.columns` 動態解欄本來就 fail-soft)。

export const UNIFIED_REPORT_METRICS = [
  // 共同(Google + Meta)
  'impressions',
  'clicks',
  'cost_micros',
  'conversions',
  'conversions_value',
  'video_views',
  'ctr',
  'average_cpc_micros',
  'video_view_rate',
  'video_p25',
  'video_p50',
  'video_p75',
  'video_p100',
  'video_p25_rate',
  'video_p50_rate',
  'video_p75_rate',
  'video_p100_rate',
  // Meta 專屬(Google 列 null)
  'link_clicks',
  'reach',
  'frequency',
  'engagements',
  'engagement_rate',
  'post_comments',
  'post_shares',
  'post_saves',
  'purchase',
  'all_purchase',
  'add_to_cart',
  'checkout',
  'registration',
  'application',
  'message_started',
  'video_plays',
  'video_3sec_views',
  'video_hook_rate',
  'video_p95',
  'video_p95_rate',
  'video_avg_time_watched',
] as const;
export type UnifiedReportMetric = (typeof UNIFIED_REPORT_METRICS)[number];

export const UNIFIED_REPORT_GROUP_BYS = [
  'data_date',
  'platform',
  'customer_id',
  'currency_code',
  'campaign_id',
  'ad_group_id',
  'ad_group_ad_id',
] as const;
export type UnifiedReportGroupBy = (typeof UNIFIED_REPORT_GROUP_BYS)[number];

export interface UnifiedReportFilters {
  /** 不帶 / 空 = 兩個都查。 */
  platforms?: AdsPlatform[];
  /** 我方 ad_accounts doc id;BE 各平台各自翻成 customer_id 再交集(不需要 customerIds)。 */
  adAccountIds?: string[];
  customerIds?: string[];
  campaignIds?: string[];
}

export interface UnifiedReportQueryRequest {
  metrics: UnifiedReportMetric[];
  groupBy: UnifiedReportGroupBy[];
  dateRange: { from: string; to: string };
  filters?: UnifiedReportFilters;
  orderBy?: Array<{ field: string; direction: 'ASC' | 'DESC' }>;
  limit?: number;
  cursor?: string;
}

// ===== R410(BE docs/135)— 素材成效跨媒體合併報表 `POST /api/ads/reports/assets/query` =====
//
// 只讀 BE 的快查表(每次同步後重建,晚同步 5–20 分鐘);一支端點同時查 Google + Meta,
// 合併列(相同 / 相似)可以同時含兩個媒體的成員,數字直接相加。
// ⚠️ 與統一面 `reports/query` 是**兩支不同端點**:那支做不到素材層(docs/113 line 69),
// 這支只做素材層。請求欄位也不同(`kinds` / `placement_types` / `asset_refs` 只有這支有)。

/** 企劃 8 欄(BE `AssetReportMetric`;docs/135 §2)。**順序 = 表頭預設順序**。 */
export const ASSET_REPORT_METRICS = [
  'cost_micros', // response 回 `cost_amount`(帳戶幣別「元」)
  'impressions',
  'clicks', // Meta 列 = 連結點擊(CTR / CPC 也用它算)
  'video_views', // 非影片列 null
  'average_cpm_micros', // micros ÷ 1e6 才是元
  'average_cpc_micros',
  'ctr', // 0–1 比例
  'video_view_rate', // 0–1 比例;非影片列 null
] as const;
export type AssetReportMetric = (typeof ASSET_REPORT_METRICS)[number];

/** 列鍵(`group_by` 必含恰好一個)。 */
export type AssetReportRowKey = 'asset_id' | 'asset_group_key' | 'asset_similar_key';
/** `group_by` 值域:列鍵 + 三個選配切細維度。 */
export type AssetReportGroupBy = AssetReportRowKey | 'platform' | 'customer_id' | 'data_date';

/** 分頁籤(**後端篩**,跨頁正確);wire 小寫、列上的 `kind` 是大寫。 */
export const ASSET_REPORT_KINDS = ['image', 'video', 'text', 'other'] as const;
export type AssetReportKind = (typeof ASSET_REPORT_KINDS)[number];

/** 掛載層:同一顆素材在不同層是不同列,永不相加(PMax 自己一組;Meta 全部是 `ad`)。 */
export const ASSET_PLACEMENT_TYPES = ['ad', 'pmax', 'campaign_link'] as const;
export type AssetPlacementType = (typeof ASSET_PLACEMENT_TYPES)[number];

/** 一顆素材的身分 —— 兩平台的 id 是不同號碼空間,**永遠帶 platform**。 */
export interface AdAssetRef {
  platform: AdsPlatform;
  assetId: string;
}

export interface AssetReportFilters {
  /** 不帶 / 空 = 兩個都查。 */
  platforms?: AdsPlatform[];
  customerIds?: string[];
  adAccountIds?: string[];
  /** 不帶 / 空 = 全部分頁。 */
  kinds?: AssetReportKind[];
  /** 不帶 / 空 = 全部掛載層。 */
  placementTypes?: AssetPlacementType[];
  /** 只對 `asset_similar_key` 有效(BE 預設 normal)。 */
  similarityLevel?: SimilarityLevel;
  /** 名稱 / 文案 / 投放歸屬名稱 contains,1–100 字(空字串 BE 422 → mapper 不帶)。 */
  assetNameQuery?: string;
  /** ≤50;展開合併列成員用(拿 `member_refs` 回打)。 */
  assetRefs?: AdAssetRef[];
  /** 預設 false(窗內零曝光的素材也出列,數字 0)。 */
  onlyWithDelivery?: boolean;
}

export interface AssetReportsQueryRequest {
  metrics: AssetReportMetric[];
  groupBy: AssetReportGroupBy[];
  dateRange: { from: string; to: string };
  filters?: AssetReportFilters;
  orderBy?: Array<{ field: string; direction: 'ASC' | 'DESC' }>;
  limit?: number;
  cursor?: string;
}

/**
 * 同既有報表 envelope + 快查表兩欄。
 *
 * ⚠️ `dataAsOf` 在這支**可能是 null**:空頁(0 列,含翻過最後一頁)時 BE 回 null
 * —— 呼叫端沿用前一頁的值(docs/135 §1 2026-10-11 補充),不可顯「資料時間:null」。
 */
export interface AssetReportsQueryResponse extends Omit<ReportQueryResponse, 'dataAsOf'> {
  dataAsOf: string | null;
  /** 快查表最近一次重建時間(ISO);空結果可能 null。 */
  martBuiltAt: string | null;
  /**
   * `false` = 快查表還沒建出來(第一次部署;BE 已派重建)→ 顯「資料準備中,稍後再試」。
   * **不是錯誤**(200 + 空列)。缺欄(舊 build)→ `true`。
   */
  martReady: boolean;
}

export interface ReportQueryResponse {
  rows: ReportRow[];
  schema: { columns: ReportSchemaColumn[] };
  /**
   * 資料時間。R302.1(docs/113,`dev-update-v22-43`)起統一面的語意 = **各 in-scope 平台先取最新、再跨平台取最舊**
   * (沒有任何 watermark 的平台不參與;曾投放後停投的帳戶不再拖住整頁)。Google 面維持 docs/38 語意。
   */
  dataAsOf: string;
  /**
   * R302.1(docs/113 Response)— 統一面專屬:`{ google?: ISO | null, meta?: ISO | null }`,**只含 in-scope 平台**;
   * 值 = 該平台 in-scope 帳戶 metrics watermark 的最新時間,`null` = 該平台沒有任何 watermark(零投放)。
   * Google 面 / 舊 build → `null`(顯示端退回 `dataAsOf`)。
   */
  dataAsOfByPlatform: Partial<Record<AdsPlatform, string | null>> | null;
  cacheHit: boolean;
  bqJobId: string;
  bytesBilled: number;
  nextCursor: string | null;
  /**
   * 符合本次查詢條件的**總列數**(R246;不受 `limit` / `cursor` 影響,第一頁就給)。
   *
   * BE 用 `COUNT(*) OVER ()` 視窗算,不多掃資料。空結果且首頁 → `0`;無法判定 → `null`。
   * ⚠️ 有了它才有真的分母 —— 在此之前 FE 只能顯示「已載入 N 筆」(翻完才知道總數)。
   * 舊 build 沒這欄 → mapper 補 `null`(顯示端退回「已載入」措辭)。
   */
  totalRows: number | null;
  /**
   * 非可加標記(docs/38 §2b):素材成效查詢固定回 `"asset_grain_non_additive"`,
   * 其餘查詢 null。語意 = 素材數字是 Google 的「服務視角」,一次曝光/點擊會記到該次
   * 出現的**每顆**素材上 → 跨素材加總 ≠ campaign/ad 層總計(**不是 bug,不要對帳**);
   * FE 呈現:素材列表不顯示合計列,並標注非可加說明。
   */
  aggregationCaveat: string | null;
}

// ===== T9(docs/51)— M-11 AEI 疲乏聚合 =====

/** AEI 自訂視窗(選填;`from`/`to` 同給或同省,只給一邊 → 422)。省略 → 後端最近 8 完整週。 */
export interface AeiRange {
  /** `YYYY-MM-DD`,含端點。 */
  from: string;
  to: string;
}

/**
 * AEI 素材鍵來源(BE `aei_service.AeiAssetSource`;docs/89 §2)。
 *
 * - `platform`(**BE 預設**)= closed-loop `our_asset_id` —— ingestion 端至今全 NULL,
 *   **結果恆空**。R230 素材成效表 AEI 欄全「—」的根因就是沒帶下面那個值。
 * - `google` = Google 原生 `asset_id`(素材成效 S1/S2 那批資料)—— 素材頁要帶這個。
 */
export const AEI_ASSET_SOURCES = ['platform', 'google'] as const;

export type AeiAssetSource = (typeof AEI_ASSET_SOURCES)[number];

/** `GET /reports/aei` 的查詢參數(全選填;省略 = BE 預設 platform + 最近 8 完整週)。 */
export interface AeiQuery {
  range?: AeiRange;
  /** 缺省 = BE 的 `platform`(恆空);素材成效表一律帶 `'google'`。 */
  assetSource?: AeiAssetSource;
  /** `google` 模式的掛載層,**必須與列表同 scope**(BE 單層鎖);`platform` 模式忽略。 */
  assetLinkScope?: AssetLinkScope;
}

/**
 * 單一素材 AEI 分數拆解;各 `*Score` 為未加權原始分,`null` = 該變項無資料。
 *
 * **素材鍵二擇一**(依請求的 `assetSource`):`ourAssetId`(platform closed-loop)或
 * `assetId`(Google 原生)。BE 2026-08-12 起兩欄皆 nullable —— 用哪一邊由呼叫端自己
 * 知道(它決定了 `asset_source`),**不要對 null 那邊做 fallback**(會把兩套 id 混在
 * 同一個 Map 裡)。
 */
export interface AeiAssetScore {
  ourAssetId: string | null;
  /** Google 原生素材 id(`assetSource: 'google'` 時有值)。 */
  assetId: string | null;
  /** 0–100,**越高越疲乏**。 */
  score: number;
  frequencyScore: number | null;
  ctrScore: number | null;
  vtrScore: number | null;
  vcrScore: number | null;
  cpcScore: number | null;
  /** 缺值變項名(目前一定含 `"frequency"`,見 docs/51 §現況限制)。 */
  missing: string[];
  weeksEvaluated: number;
  /** AEI(Wx) − AEI(Wx-1);< 2 週 → null。 */
  weekOverWeekDelta: number | null;
  /** delta ≥ +10(§8 惡化門檻)。 */
  isWorsening: boolean;
  /**
   * 出生週 / 評估週的週一(ISO date;ADR-047,BE 2026-09-14)。
   * 「評估至 X 週」標示用;migration 前的舊落表列是 null,下一輪同步後即有值。
   */
  baselineWeekStart: string | null;
  evalWeekStart: string | null;
}

/** AEI 分布桶(永遠 5 個等寬桶;Σ count = assetCount)。 */
export interface AeiBucket {
  label: string;
  rangeMin: number;
  rangeMax: number;
  count: number;
}

/** 評估週 VTR/VCR 跨素材平均(0–1 rate;null = 無資料)。 */
export interface AeiVtrVcr {
  avgVtr: number | null;
  avgVcr: number | null;
  sampleSize: number;
}

/** 週趨勢點(依週序舊→新;`avgAei` null = 該週無素材貢獻)。 */
export interface AeiWeeklyPoint {
  isoYear: number;
  isoWeek: number;
  weekStart: string;
  avgAei: number | null;
  assetCount: number;
}

export interface GroupAeiReport {
  windowFrom: string;
  windowTo: string;
  assetCount: number;
  /** 依 score 高→低(最疲乏在前),可直接當「需關注清單」。 */
  assets: AeiAssetScore[];
  distribution: AeiBucket[];
  vtrVcr: AeiVtrVcr;
  weeklyTrend: AeiWeeklyPoint[];
  /** 最保守資料新鮮度(可 null)。 */
  dataAsOf: string | null;
  /**
   * 分數的計算基礎(docs/89 §2)。`"google_asset_ctr_dominant"` = Google 素材面
   * (Google 不提供素材層曝光頻次、VCR 亦缺 → **CTR 衰退主導**);platform 面 = null。
   * **跨 basis 的分數不可互比** → UI 要把這件事講出來(tooltip)。
   */
  computationBasis: string | null;
  /**
   * `true` = 素材數命中 BE `MAX_AEI_READ_ASSETS` 被截斷(2026-09-13 finding B):
   * `assets` 只是**最疲乏的前 N 顆**,且 `assetCount` / `distribution` / `vtrVcr`
   * 都是這 N 顆的統計。呈現端 true 時必須標明「僅前 N 顆」—— 截斷不可以是靜默的。
   * 舊 BE 缺欄 → false(mapper 補),行為與截斷欄存在前一致。
   */
  hasMore: boolean;
}

// ===== T7(docs/52)— 推素材上 Google Ads 素材庫(M-04/M-07)=====

/** ga_upload 狀態;影片非同步(UPLOADING→PROCESSING),圖/文同步(直接 SUCCEEDED/FAILED)。 */
export type GaUploadStatus = 'PENDING' | 'UPLOADING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED';

/** `POST /assets/imports` body 內單一素材庫項目參照。 */
export interface AssetImportItemRef {
  libId: string;
  itemId: string;
}

/** `POST /assets/imports` request。 */
export interface CreateAssetImportsRequest {
  /** GA 數字 customer_id(6–16 位)。 */
  customerId: string;
  /** 1–100 筆素材庫項目。 */
  items: AssetImportItemRef[];
  /**
   * 強制重新上傳(R407;BE 2026-10-10,與 Meta `force` 同名同義)。`true` = 跳過
   * 「同素材同帳戶已上傳過 → 沿用」那道 dedup,一律再傳一份;給「素材在廣告帳戶被誤刪」用。
   *
   * ⚠️ **整批套用**:只把要強制的那幾筆放進 `items`。⚠️ **沒有重複上傳防護** —— 連送兩次
   * = 帳戶多一顆一樣的素材,呼叫端要自己擋連按。⚠️ 舊 BE `extra="forbid"`:帶這個鍵 =
   * 整批 422,所以 `false` / 缺 → **鍵不上 wire**,入口另有 gate
   * (`ADS_UPLOAD_FORCE_SUPPORTED`,`services/adsService.ts`)。
   */
  force?: boolean;
}

/** `POST /assets/imports` 回的 per-item 結果。 */
export interface AssetImportOutcome {
  libId: string;
  itemId: string;
  assetKind: string | null;
  status: GaUploadStatus | string;
  uploadId: string | null;
  gaResourceName: string | null;
  /** dedup:該 item 已成功推過同帳戶 → true(reuse,不重打 GA)。 */
  alreadyExisted: boolean;
  errorClass: string | null;
}

export interface CreateAssetImportsResponse {
  imports: AssetImportOutcome[];
}

/** 單筆 ga_upload 對外 shape(不 leak client_request_id / operation_id 等內部欄)。 */
export interface GaUpload {
  uploadId: string;
  libId: string;
  itemId: string;
  platform: string;
  customerId: string;
  assetKind: string;
  status: GaUploadStatus | string;
  gaResourceName: string | null;
  youtubeVideoId: string | null;
  errorClass: string | null;
  progressHint: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}


// ===== M5(BE docs/118)— 把我方素材推上 **Meta** 廣告帳戶素材庫 =====
//
// 形狀與 Google T7(`CreateAssetImportsRequest` / `AssetImportOutcome`)刻意鏡射,
// 但 **§C 有八處不同**,其中四處踩到就是 422 或使用者看不懂的失敗:
//
//   1. 批次上限 **50**(Google 100)—— 圖片是同步打 Meta,批太大會拖到 HTTP 逾時
//   2. 來源**恰填一組**,混填 → 422(Google T7 是靜默忽略多的欄)
//   3. 同一批**不可有重複來源** → 422(重複會真的各傳一次,客戶帳戶多一顆一樣的素材)
//   4. **文案不能上傳** —— Meta 沒有「文字素材」這種物件
//
// ⚠️ **這不是「發佈」**:只把素材推進客戶帳戶的素材庫(`adimages` / `advideos`),
// 不建廣告、不投放、不扣點。

/** Meta 上傳的來源:素材庫 item(A)或畫布產物(B);**恰填一組**。 */
export type MetaUploadItemRef =
  | { libId: string; itemId: string; projectId?: never; jobId?: never; assetId?: never }
  | { projectId: string; jobId: string; assetId: string; libId?: never; itemId?: never };

/**
 * `POST /ads/meta/assets/uploads` request。
 *
 * ⚠️ `items` 送出前 FE 要**自己去重**(見 {@link META_UPLOAD_BATCH_MAX} 附近的說明)。
 */
export interface CreateMetaAssetUploadsRequest {
  /** 目標 Meta 廣告帳戶(`act_` 後面的數字);`^\d{6,20}$`(Google 是 6–16)。 */
  customerId: string;
  /** 1–{@link META_UPLOAD_BATCH_MAX} 筆。 */
  items: MetaUploadItemRef[];
  /**
   * 強制重新上傳(R407;與 Google `force` 同名同義,見 {@link CreateAssetImportsRequest.force})。
   * Meta 側差異:**影片正在傳**的那筆照樣回既有 `upload_id`(`alreadyExisted=true`),
   * 不會並行再傳一支;圖片的 `image_hash` 是內容雜湊,重傳通常回同一個 hash(正常)。
   */
  force?: boolean;
}

/**
 * 單次可送的筆數上限(BE docs/118 §A.1)。
 *
 * ⚠️ **不是 Google 的 100**:Meta 的圖片在 POST 的同一個 HTTP response 內同步打完,
 * 批太大會拖到 HTTP 逾時,所以 BE 收在 50。FE 要在送出前分批,超過就是 422。
 */
export const META_UPLOAD_BATCH_MAX = 50;

/**
 * `POST /ads/meta/assets/uploads` 的 per-item 結果(順序與 request `items` 一致)。
 *
 * ⚠️ 來源欄是 **echo**:填了哪一組就回哪一組,另一組為 null —— 對回去可以靠索引,
 * 也可以靠來源欄,兩者都行。
 */
export interface MetaAssetUploadOutcome {
  libId: string | null;
  itemId: string | null;
  projectId: string | null;
  jobId: string | null;
  assetId: string | null;
  /** `IMAGE` | `VIDEO` | `TEXT`(拒收時)| null(來源校驗就失敗) */
  assetKind: string | null;
  /** 圖片同步出終態;影片回 `PENDING`(或重送命中「正在傳」時的當前狀態)。 */
  status: GaUploadStatus | string;
  /** null = 還沒建紀錄(來源 / 種類校驗就失敗)—— 這種筆**不能拿去輪詢**。 */
  uploadId: string | null;
  /** 圖片成功才有;**綁該廣告帳戶**,跨帳戶不可重用。 */
  imageHash: string | null;
  /** 影片成功才有;同樣綁該帳戶。 */
  videoId: string | null;
  /**
   * `true` = 命中既有紀錄(已傳成功、或影片正在傳),**沒有重打 Meta**。
   *
   * ⚠️ dedup 是 **per-(素材, 帳戶)**:同一顆素材推到另一個 Meta 帳戶時這裡會是 `false`
   * 而且**應該**重新上傳 —— 顯示成「已上傳過」就是騙人(那個帳戶裡並沒有)。
   */
  alreadyExisted: boolean;
  /** `FAILED` 才有;當**開放字串**讀,不要 exhaustive switch(F118-9)。 */
  errorClass: string | null;
}

export interface CreateMetaAssetUploadsResponse {
  /** BE 回 `meta_ads`;留著給觀測,不要拿它分支(平台由你打的 base path 決定)。 */
  platform: string;
  uploads: MetaAssetUploadOutcome[];
}

/** `GET /ads/meta/assets/uploads/{upload_id}` 的單筆狀態(輪詢用)。 */
export interface MetaAssetUpload {
  uploadId: string;
  platform: string;
  libId: string | null;
  itemId: string | null;
  projectId: string | null;
  jobId: string | null;
  assetId: string | null;
  customerId: string;
  assetKind: string;
  status: GaUploadStatus | string;
  imageHash: string | null;
  videoId: string | null;
  errorClass: string | null;
  progressHint: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * `META_TEXT_STANDALONE_UNSUPPORTED` —— 選到文案素材時的必然失敗。
 *
 * ⚠️ **正確做法是在選取器就 disable 文案素材**(F118-3),不要讓使用者選了才失敗:
 * Meta 根本沒有「文字素材」這種物件,文案是廣告 creative 的欄位。這顆常數只是讓
 * 呈現層認得它、給一句講得清楚的話,不是讓你把它當可重試的錯。
 */
export const META_TEXT_UPLOAD_UNSUPPORTED = 'META_TEXT_STANDALONE_UNSUPPORTED';

/**
 * 這一筆結果可不可以拿去輪詢。
 *
 * ⚠️ 判準是 **`uploadId` 有值 + 狀態還沒到終態**,不是「status === PENDING」——
 * 影片重送命中「正在傳」時 BE 回的是 doc 現值(`UPLOADING` / `PROCESSING`),
 * 只認 `PENDING` 會讓那些筆永遠不更新(F118-5)。
 */
export function metaUploadNeedsPolling(o: {
  uploadId: string | null;
  status: GaUploadStatus | string;
}): o is { uploadId: string; status: string } {
  if (o.uploadId === null) return false;
  return o.status !== 'SUCCEEDED' && o.status !== 'FAILED';
}

// ===== I1(docs/89 §3b)— 廣告帳戶素材「匯入」我們的素材庫 =====
//
// ⚠️ **方向與 T7 相反、路徑差一個 s**:
//   - T7(既有):`POST /assets/imports`(複數)= **我們的素材庫 → Google Ads**
//     (`importAssets` / `CreateAssetImportsRequest`)
//   - I1(本段):`POST /assets/import`(單數)= **Google Ads → 我們的素材庫**
//     (`importAdAssets` / 下面這組型別)
// 兩者的 service method 名字刻意都帶方向,不要只看到 "import" 就接上去。

/** 匯入 job 狀態(BE `asset_import_service.ImportJobStatus`)。 */
export const AD_ASSET_IMPORT_STATUSES = [
  'PENDING',
  'RUNNING',
  'DONE',
  'PARTIAL',
  'FAILED',
] as const;

export type AdAssetImportStatus = (typeof AD_ASSET_IMPORT_STATUSES)[number];

/**
 * per-asset 結果狀態。
 *
 * ⚠️ **`SKIPPED_UNSUPPORTED` 不是失敗** —— 只匯入圖片與影片,TEXT / CALL 類素材
 * Google TOS 或本案範圍不收,BE 明示「不算失敗」。但 BE 的 job 終態算式只數 `DONE`
 * (`done_n == total` → DONE / 有 DONE → PARTIAL / **一個 DONE 都沒有 → FAILED**)
 * → **勾一批純文字素材匯入,job 會是 `FAILED` 而其實什麼都沒壞**。
 * FE 必須自己分辨(見 `adAssetImportOutcome`),不可直接把 FAILED 顯示成「匯入失敗」。
 */
export const AD_ASSET_ITEM_STATUSES = ['DONE', 'FAILED', 'SKIPPED_UNSUPPORTED'] as const;

export type AdAssetItemStatus = (typeof AD_ASSET_ITEM_STATUSES)[number];

/** 一批最多幾顆(BE `MAX_ASSETS_PER_IMPORT`;超量 422)。 */
export const AD_ASSET_IMPORT_MAX = 50;

// ── R390:廣告帳戶素材清單(`GET /ads/google/assets`;docs/114 §9.2)────────────
//
// served-only 母集(D-1)+ 自動庫瘦身(D-6)之後,「從未投放」的素材**不再進**
// 自動庫 `ads-lib-{gid}` —— 要讓使用者挑它們,唯一入口是這支端點的 `served=never`
// (**即時查 Google**,延遲 ~1-3s;`served` / `kind` 是 server-side 白名單,傳別的值 422;
// rate limit 60/min;404 = `AD_ACCOUNT_NOT_FOUND`)。

/** `served` 白名單(BE server-side;⚠️ 存量清理跑完後 `all` ≈ `served`)。 */
export const AD_ASSET_SERVED_FILTERS = ['all', 'served', 'never'] as const;

export type AdAssetServedFilter = (typeof AD_ASSET_SERVED_FILTERS)[number];

/** `limit` 上界(BE 白名單;超過 422)。 */
export const AD_ACCOUNT_ASSETS_LIMIT_MAX = 500;

/**
 * 帳戶上的一顆 Google 原生素材(**不是** LibraryItem —— 它還沒進任何素材庫)。
 *
 * ⚠️ `served=never` 模式下四個 enrichment 欄(`assetGroupKey` / `assetGroupName` /
 * `durationMillis` / `lastSeenAt`)**恆 null**(enrichment 是 BQ 面的,即時查 Google
 * 拿不到)—— 呈現端拿不到就不顯,不要把 null 讀成「沒有群組」。
 * ⚠️ `kind` 是開放字串(BE 白名單現值 IMAGE / VIDEO;R296 判準:不窮舉 wire 值域)。
 */
export interface AdAccountAsset {
  assetId: string;
  kind: string;
  name: string | null;
  imageUrl: string | null;
  youtubeVideoId: string | null;
  policyApprovalStatus: string | null;
  hasServed: boolean | null;
  /**
   * 非 null = 這顆已經在自動庫裡(顯「已匯入」標記;再匯只會建重複 item)。
   * ⚠️ fail-safe 方向 = 少標:讀不到照樣可勾,BE 那側頂多多一顆帶
   * `duplicate_of_item_id` 的 item(docs/89 §3c),不會壞資料。
   */
  importedItemId: string | null;
  assetGroupKey: string | null;
  assetGroupName: string | null;
  durationMillis: number | null;
  lastSeenAt: string | null;
}

/** `GET /ads/google/assets` 回應(`total` = **本次回傳數**,不是全宇宙數 —— D-6)。 */
export interface AdAccountAssetsResult {
  customerId: string;
  servedFilter: string;
  total: number;
  assets: AdAccountAsset[];
}

export interface AdAssetImportRequest {
  /** Google 數字 customer_id(**素材 id 是 per-customer 的**,一定要跟著送)。 */
  customerId: string;
  /** Google 原生 asset id,1–50 顆。 */
  assetIds: string[];
  /** 目的素材庫 id(`AssetLibrarySummary.libId`);非本 group → BE 403。 */
  libraryId: string;
}

/** `POST /assets/import` 202 回應。 */
export interface AdAssetImportAccepted {
  jobId: string;
  assetCount: number;
  status: AdAssetImportStatus;
}

/** per-asset 結果(`results[assetId]`)。 */
export interface AdAssetImportItemResult {
  status: AdAssetItemStatus | string;
  /** 成功時的素材庫 item id;`SKIPPED` / `FAILED` 為 null。 */
  itemId: string | null;
  error: string | null;
  /**
   * 撞庫標記(docs/89 §3c)—— **不是失敗**:新 item 照建、檔案照上,只是同庫內
   * 已經有相同內容的素材,這裡指向**最早那顆**。判定:圖片 = 檔案 sha256;
   * YouTube = 影片 id。null = 沒撞庫。
   */
  duplicateOfItemId: string | null;
}

/** `GET /assets/import/{job_id}` 回應。 */
export interface AdAssetImportJob {
  jobId: string;
  status: AdAssetImportStatus;
  total: number;
  /** ⚠️ **BE 只數 `DONE`** —— 進度分母要用 settled 才會走到底(見下方 helper)。 */
  done: number;
  results: Record<string, AdAssetImportItemResult>;
}

/** job 是否已到終態(輪詢可停)。 */
export function isAdAssetImportTerminal(s: AdAssetImportStatus): boolean {
  return s === 'DONE' || s === 'PARTIAL' || s === 'FAILED';
}

/**
 * job → 誠實的結果拆解(**不要直接用 `job.status` 對使用者說話**)。
 *
 * BE 的終態只看「有沒有 DONE」,所以「全部都是不支援的類型」會被算成 `FAILED`。
 * 這支把三種結果分開數,呼叫端據此決定文案:有 `failed` 才是真的失敗,
 * 只有 `skipped` 是「這些類型不支援」。
 *
 * `settled` 用於進度分母(R213 `checkBatchPct` 同型:只數成功的話,有跳過/失敗時
 * 進度條永遠到不了底)。
 */
export function adAssetImportOutcome(job: AdAssetImportJob): {
  done: number;
  failed: number;
  skipped: number;
  /** 成功入庫但**同庫內已有相同內容**的筆數(docs/89 §3c;算在 `done` 裡面,不另計)。 */
  duplicates: number;
  settled: number;
  /** 0–100(整數);`total` 為 0 → 0。 */
  percent: number;
} {
  let done = 0;
  let failed = 0;
  let skipped = 0;
  let duplicates = 0;
  for (const r of Object.values(job.results ?? {})) {
    if (r.status === 'DONE') {
      done += 1;
      // 撞庫是成功的一種(檔案有上、item 有建),只是內容重複 → 不從 done 扣掉
      if (r.duplicateOfItemId) duplicates += 1;
    } else if (r.status === 'SKIPPED_UNSUPPORTED') skipped += 1;
    else failed += 1;
  }
  const settled = done + failed + skipped;
  const total = job.total > 0 ? job.total : 0;
  return {
    done,
    failed,
    skipped,
    duplicates,
    settled,
    percent: total > 0 ? Math.min(100, Math.round((settled / total) * 100)) : 0,
  };
}

// ===== sessionStorage key =====

/**
 * sessionStorage key — `/oauth/link` 200 DISCOVERED state 完 FE 寫,select 頁讀。
 *
 * Value(JSON;**R238 起只存 discovery_id,別的什麼都不存**):
 * ```json
 * { "discovery_id": "01HZ..." }
 * ```
 *
 * **R237(F90-1)**:R42.1–R236 存的是整包 `discovered_accounts` —— 4000+ 帳號會炸
 * sessionStorage 配額,而且把**別的租戶的客戶 id + 帳戶名**留在瀏覽器持久層
 * (R226 資安審計點名的那把 key)。帳戶清單一律現打
 * `GET /discoveries/{id}/accounts` 分頁/搜尋 endpoint。
 *
 * **R238(Bryan「之前那些快取啥的就不用了」)**:R237 過渡期曾存 `discovered_total`
 * / `truncated` 當 meta 快取 —— 兩階段(第一層秒回)後 `discovered_total` 語意變成
 * 「第一層筆數」、truncated 由分頁回應自帶,快取那份只會過期誤導 → 收掉。
 * 讀端對所有舊形狀 tolerant(只取 `discovery_id`)。
 *
 * R42.1 取代 R42.0 的 `ads.oauth.discoveryId` string-only key(state binding 不需要了)。
 */
export const ADS_DISCOVERY_SESSION_KEY = 'ads_discovery';

/**
 * sessionStorage key — `/ads/connect` 點 button 跳 perm OAuth 前 set,`/ads/connecting`
 * on mount 讀 + **one-shot 清掉**。用來判 grant 完回來時 `state === 'NEEDS_AUTHORIZATION'`
 * 表「用戶 Google 端取消了」的死 loop 防護(對齊 docs/31 §3.6 + backend MR !26 收尾)。
 *
 * Value(R300 起兩種):`'1'` = 一般連結 / 新增身分;`'reauth:<oauth_user_id>'` 或
 * `'reauth:1'` = 帶著 `?reauth=` 去的重新授權(docs/110)—— `/ads/connecting` 拿它判
 * 「使用者是不是換了另一個帳號來重授權」(callback 帶回的 `identity` ≠ 記下的那把)。
 * 讀寫只走 `features/ads/oauthPending.ts`。
 *
 * **R42.4 取代 R42.3 的 `?from=oauth_start` URL query marker**。改 sessionStorage
 * 原因:BE 從 perm 端 callback 完 redirect 給 FE 時 URL 可能帶 `?session_token=...`
 * 之類雜訊 query,用 URL query 做 marker 容易撞污染;sessionStorage 乾淨且 one-shot 清。
 */
export const ADS_OAUTH_PENDING_SESSION_KEY = 'ads_oauth_pending';

/**
 * sessionStorage key — `/ads/accounts/select` 「確認」按下後,把 link + unlink batch
 * 結果暫存,redirect `/ads/accounts` 後 page mount 讀 + one-shot 清 + 顯 info banner。
 *
 * **R42.12 新加**(對齊 backend spec `already_existed` toast 區分新建 vs reuse):
 *
 * Value shape(JSON):
 * ```ts
 * { newCount: 3, reusedCount: 2, unlinkedCount: 1 }
 * ```
 *
 * 任一欄為 0 / undefined 表 banner 不顯該段;三個都 0 / undefined → page 不顯 banner。
 */
export const ADS_LINK_RESULT_SESSION_KEY = 'ads_link_result';

/**
 * `ads_link_result` sessionStorage payload schema。
 *
 * **不對齊 BE wire format** — 純 FE 端「對 page 顯 banner」一次性提示資料,不入 BE。
 */
export interface AdsLinkResultHint {
  /** 本次新建 link 帳號數(POST `/accounts` response `already_existed=false` count) */
  newCount: number;
  /** 本次 reuse 既有帳號數(POST `/accounts` response `already_existed=true` count) */
  reusedCount: number;
  /** 本次解除帳號數(DELETE `/accounts` response `deleted.length`) */
  unlinkedCount: number;
  /** R299 — 這批是哪個平台的(toast 文案「已綁定 N 個 Meta 廣告帳戶」);舊 hint 缺欄 → google。 */
  platform?: AdsPlatform;
  /** R300 — `rebound=true` 的筆數(換帳號重授權後改綁到新身分;docs/110 P4)。 */
  reboundCount?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// R325 — 跨來源素材分群(BE docs/117;`POST /api/ads/assets/groups`)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 分群面的素材來源。`google` / `meta` = 廣告帳戶裡的素材;`platform` = 我方平台
 * 自產(AI 生成 / 上傳 / 範本)。
 *
 * ⚠️ 這是 **request** 的值域(BE `Literal`,送別的值 422);response 端的 `source`
 * 欄一律當開放字串讀(R296:wire 值域是開放的)。
 */
export type AssetGroupSource = 'google' | 'meta' | 'platform';

export const ASSET_GROUP_SOURCES: readonly AssetGroupSource[] = ['google', 'meta', 'platform'];

/**
 * 分群查詢(FE 形狀;wire 對應 docs/117 §1 Request)。
 *
 * ⚠️ BE request 是 `extra="forbid"`:**多送一個欄 = 整包 422**,所以 DTO 轉換
 * (`assetGroupsQueryToDto`)是白名單組裝,絕不可 spread 任何頁面物件進來。
 */
export interface AssetGroupsQuery {
  /** 相似鬆緊,預設 strict。⚠️ 影片三檔同鍵(換檔只影響圖片與文字家族)。 */
  similarityLevel?: SimilarityLevel;
  /** 不帶 / 空 = 三個來源都查。 */
  sources?: AssetGroupSource[];
  /**
   * **成效窗**(`includeMetrics=true` 時必帶;BE 422)。⚠️ 它**不篩素材列** ——
   * `last_seen` 每輪同步都會刷新,拿它篩活躍素材會把所有還在投放的素材篩掉
   * (docs/117 §4 的坑紀錄)。
   */
  dateRange?: { from: string; to: string };
  /** 只能**縮小**綁定集合(BE 取交集);平台列不受影響。≤50。 */
  customerIds?: string[];
  /** 家族最少成員數(1–1000,BE 預設 2;看單顆自成一組傳 1)。 */
  minMembers?: number;
  /** 只回跨來源家族(owner 的原始問題:「平台做的圖有沒有真的被投放」)。 */
  onlyCrossSource?: boolean;
  /** 成效選配,預設關(成效表是 9 GB 級分區表;docs/117 §0-5 成本量測)。 */
  includeMetrics?: boolean;
  /** 每個家族回幾筆 members(1–100,BE 預設 20;截斷是 per-來源輪替)。 */
  memberLimit?: number;
  /** 1–200,BE 預設 50。 */
  limit?: number;
  /** 本端點專屬 cursor;換任何條件要從第一頁重查(422 `INVALID_CURSOR`)。 */
  cursor?: string;
}

/** 縮圖。`url` 可直接 `<img>`(仍要過信任判準);`gcsUri` 是我方 bucket,要簽過才看得到。 */
export interface AssetGroupThumb {
  source: string | null;
  url: string | null;
  gcsUri: string | null;
}

/** 家族成員(一顆素材)。response 端的字串值域一律開放(R296)。 */
export interface AssetGroupMember {
  source: string;
  /** 投放平台;平台自產素材恆 null(它未必被投放到任何廣告平台)。 */
  platform: string | null;
  /** Google CID / Meta account_id;平台列恆 null。 */
  customerId: string | null;
  assetId: string;
  /** IMAGE / VIDEO / TEXT / COMPOSE / OTHER(開放讀)。 */
  kind: string;
  name: string | null;
  thumb: AssetGroupThumb;
  /** 平台列 = asset_id 本身;Google = 匯入過才有;**Meta 恆 null**(docs/117 §4)。 */
  libraryItemId: string | null;
  /** 只有平台成員有(「AI 生成」/「上傳」…)。 */
  sourceLabel: string | null;
  durationMillis: number | null;
  /** ⚠️ Google 成員**恆 null**(BE 誠實留白,顯「—」不要當 1970)。 */
  firstSeen: string | null;
  lastSeen: string | null;
}

/**
 * 家族成效(`includeMetrics=true` 才可能有)。
 *
 * ⚠️ **null 是常態不是錯誤**(docs/117 §3.1 四種合法情形);拿到物件時 `platforms`
 * 一定非空(目前只會是 `["google"]`)—— 家族含 Meta / 平台成員時要標
 * 「僅含 Google 投放數字」,不要把它當整個家族的總成效。
 */
export interface AssetGroupMetrics {
  impressions: number | null;
  clicks: number | null;
  costAmount: number | null;
  conversions: number | null;
  videoViews: number | null;
  /** null = 幣別回填未追上(視為帳戶幣別),不是「沒有這個欄」。 */
  currencyCode: string | null;
  /** true = 家族跨幣別 → `costAmount` 無意義,顯「—」。 */
  currencyMixed: boolean;
  /** 真的有貢獻資料的平台;拿到物件時一定非空。 */
  platforms: string[];
}

/** 一列 = 一個相似家族。 */
export interface AssetGroupRow {
  /** 家族身分(opaque;**不要**拿 `canonical` 當家族鍵)。 */
  similarKey: string;
  canonical: string | null;
  /** 家族**全部**成員數(可能 > members.length;截斷看 memberLimit)。 */
  memberCount: number;
  /** 三欄恆在(0 也給)。計數欄可以加總;`metrics` 不行(§3.2)。 */
  sourceCounts: { google: number; meta: number; platform: number };
  kinds: string[];
  displayName: string | null;
  thumb: AssetGroupThumb;
  firstSeen: string | null;
  lastSeen: string | null;
  members: AssetGroupMember[];
  metrics: AssetGroupMetrics | null;
}

/** `POST /api/ads/assets/groups` 的結果(envelope 欄名對齊報表面,cursor **不可**互用)。 */
export interface AssetGroupsResult {
  rows: AssetGroupRow[];
  similarityLevel: string;
  /**
   * 本次**實際**涵蓋的來源 —— `meta_ads` flag off / 沒綁定的平台會被拿掉。
   * **「沒回 ≠ 沒有」**,涵蓋提示一律看這欄(對齊 `GET /api/ads/accounts` 的 `platforms`)。
   */
  sources: string[];
  /** 素材**結構**同步 watermark(不是成效);查不到 = 查詢時間。 */
  dataAsOf: string | null;
  /** 逐平台 watermark;只含 in-scope 的廣告平台(只查平台素材時是 `{}`)。 */
  dataAsOfByPlatform: Record<string, string | null>;
  nextCursor: string | null;
  /** 符合條件的家族總數(不受 limit / cursor 影響);null = BE 沒給。 */
  totalRows: number | null;
  /** 恆 `"asset_grain_non_additive"` —— 顯示合計時必須標「非去重總和」(§3.2)。 */
  aggregationCaveat: string;
}
